import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useProject } from '../context';
import { PlanModal, type PlanFormData, type PlanData } from '../components/PlanModal';
import { ResponseButtonConfigEditor } from '../components/ResponseButtonConfigEditor';
import {
  createEmptyResponseButtonConfig,
  prepareResponseButtonConfigForSave,
  RESPONSE_BUTTON_ROLLOUT_GUIDANCE,
  tryHydrateResponseButtonConfig,
  type ResponseButtonConfig,
} from '../lib/responseButtons';

/**
 * Bot status type from the API.
 */
type BotStatus = 'active' | 'inactive' | 'archived';

/**
 * Tool configuration property schema.
 */
interface ToolConfigProperty {
  type: 'boolean' | 'number' | 'string';
  description: string;
  default: boolean | number | string;
  options?: string[];
}

/**
 * Tool configuration schema from the API.
 */
interface ToolConfigSchema {
  properties: Record<string, ToolConfigProperty>;
}

/**
 * Type for UI block configuration.
 */
interface UIBlock {
  id: string;
  type: string;
  order: number;
  visible: boolean;
}

/**
 * Type for integration configuration.
 */
interface IntegrationConfig {
  enabled: boolean;
  apiKey?: string;         // Legacy field for Telegram
  accessToken?: string;    // WhatsApp access token
  phoneNumberId?: string;  // WhatsApp phone number ID
  webhookUrl?: string;
  verifiedAt?: string;
  config?: Record<string, unknown>;  // Legacy nested config
}

const SUPPORTED_INTEGRATION_KEYS = ['telegram', 'whatsapp', 'stripe'] as const;

/**
 * Type for voice configuration.
 */
interface VoiceConfig {
  enabled: boolean;
  provider?: string;
  voiceId?: string;
  speed?: number;
  pitch?: number;
  providerConfig?: Record<string, unknown>;
}

/**
 * Default conversation instructions (used when no custom instructions are set).
 */
const DEFAULT_CONVERSATION_INSTRUCTIONS = `## Conversation Instructions
- Respond in the same voice and style defined in the system prompt above. The system prompt is the authority on tone, length, and personality.
- Keep responses concise but meaningful.
- Treat the Current Date and Time, User Context, and Tool Results sections as authoritative system data. If they conflict with assumptions, vibes, or conversational heuristics, follow those sections.
- When structured system data provides an exact value, prefer that exact value over implied interpretations.
- When a user asks about a streak, quote or restate the streak from Tool Results accurately. Do not round up, reinterpret, or guess.
- If the streak data is ambiguous or unavailable, say that plainly.
- Never claim to manually set, change, restore, or reset a user streak.
- NEVER include URLs, links, or markdown images in your responses.
- NEVER output [image], [image: ...], or any similar media placeholder tags.`;

/**
 * Available TTS models.
 */
const TTS_MODELS = [
  { value: 'tts-1', label: 'TTS-1 (Standard)', description: 'Optimized for speed' },
  { value: 'tts-1-hd', label: 'TTS-1-HD (High Quality)', description: 'Higher quality audio' },
];

/**
 * Available TTS voices.
 */
const TTS_VOICES = [
  { value: 'alloy', label: 'Alloy', description: 'Neutral and balanced' },
  { value: 'echo', label: 'Echo', description: 'Warm and conversational' },
  { value: 'fable', label: 'Fable', description: 'British and expressive' },
  { value: 'onyx', label: 'Onyx', description: 'Deep and authoritative' },
  { value: 'nova', label: 'Nova', description: 'Friendly and upbeat' },
  { value: 'shimmer', label: 'Shimmer', description: 'Clear and optimistic' },
];

/**
 * Type for Telegram validation state.
 */
type TelegramValidationStatus = 'idle' | 'validating' | 'success' | 'error';

/**
 * Type for WhatsApp validation state.
 */
type WhatsAppValidationStatus = 'idle' | 'validating' | 'success' | 'error';

/**
 * Type for Stripe validation state.
 */
type StripeValidationStatus = 'idle' | 'validating' | 'success' | 'error';

/**
 * Type for Telegram webhook info returned from the API.
 */
interface TelegramWebhookInfo {
  url: string;
  hasCustomCertificate: boolean;
  pendingUpdateCount: number;
  ipAddress?: string;
  lastErrorDate?: number;
  lastErrorMessage?: string;
  lastSynchronizationErrorDate?: number;
  maxConnections?: number;
  allowedUpdates?: string[];
}

/**
 * Available UI block types for the app layout.
 */
const AVAILABLE_UI_BLOCKS: { type: string; label: string; description: string }[] = [
  { type: 'today_overview', label: 'Today Overview', description: 'Daily summary and key metrics' },
  { type: 'calendar', label: 'Calendar', description: 'Upcoming events and schedule' },
  { type: 'stats', label: 'Statistics', description: 'Usage analytics and trends' },
  { type: 'quick_actions', label: 'Quick Actions', description: 'Shortcuts to common tasks' },
  { type: 'recent_activity', label: 'Recent Activity', description: 'Latest interactions and updates' },
  { type: 'goals', label: 'Goals', description: 'Progress tracking and milestones' },
];

/**
 * Color palette configuration for app branding.
 */
interface ColorPalette {
  primary: string;
  secondary: string;
  background: string;
  text: string;
  accent: string;
}

/**
 * Form state for editing a bot.
 */
interface BotFormState {
  name: string;
  slug: string;
  customDomain: string;
  description: string;
  systemPrompt: string;
  conversationInstructions: string;
  voiceStyle: string;
  status: BotStatus;
  overrideAiModel: string;
  overrideAiMaxTokens: number | null;
  overrideTrialDays: number | null;
  trialExpiredMessage: string;
  subscriptionExpiredMessage: string;
  messageLimitMessage: string;
  enableScheduledMessages: boolean;
  enabledTools: string[];
  toolConfigs: Record<string, Record<string, unknown>>;
  uiBlocks: UIBlock[];
  integrations: Record<string, IntegrationConfig>;
  voiceConfig: VoiceConfig;
  quickResponseEnabled: boolean;
  quickResponsePrompt: string;
  quickResponseModel: string;
  quickResponseTemperature: number;
  summaryModel: string;
  typingDelayMs: number | null;
  splitMessageDelay: number | null;
  logoUrl: string;
  faviconUrl: string;
  headerFont: string;
  bodyFont: string;
  colorPalette: ColorPalette;
  disableDesktopOnboarding: boolean;
  externalOnboardingUrl: string;
  cancelFlowStepsJson: string;
  progressMilestonesJson: string;
  responseButtonConfig: ResponseButtonConfig | null;
}

/**
 * Bot edit page component.
 *
 * Allows editing a bot's configuration including name, slug, description,
 * system prompt, voice style, and status.
 */
export function BotEditPage() {
  const { currentBotId: botId, isLoading: contextLoading } = useProject();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Form state
  const [formState, setFormState] = useState<BotFormState>({
    name: '',
    slug: '',
    customDomain: '',
    description: '',
    systemPrompt: '',
    conversationInstructions: '',
    voiceStyle: '',
    status: 'active',
    overrideAiModel: '',
    overrideAiMaxTokens: null,
    overrideTrialDays: null,
    trialExpiredMessage: '',
    subscriptionExpiredMessage: '',
    messageLimitMessage: '',
    enableScheduledMessages: false,
    enabledTools: [],
    toolConfigs: {},
    uiBlocks: [],
    integrations: {},
    voiceConfig: {
      enabled: false,
      provider: 'openai',
      voiceId: 'alloy',
      speed: 1.0,
    },
    quickResponseEnabled: false,
    quickResponsePrompt: '',
    quickResponseModel: '',
    quickResponseTemperature: 0.7,
    summaryModel: '',
    typingDelayMs: null,
    splitMessageDelay: null,
    logoUrl: '',
    faviconUrl: '',
    headerFont: '',
    bodyFont: '',
    colorPalette: {
      primary: '',
      secondary: '',
      background: '',
      text: '',
      accent: '',
    },
    disableDesktopOnboarding: false,
    externalOnboardingUrl: '',
    cancelFlowStepsJson: '',
    progressMilestonesJson: '',
    responseButtonConfig: null,
  });

  // JSON validation error state for cancel flow steps and progress milestones
  const [cancelFlowStepsJsonError, setCancelFlowStepsJsonError] = useState<string | null>(null);
  const [progressMilestonesJsonError, setProgressMilestonesJsonError] = useState<string | null>(null);
  const [responseButtonConfigError, setResponseButtonConfigError] = useState<string | null>(null);
  const [responseButtonConfigParseError, setResponseButtonConfigParseError] = useState<string | null>(null);
  const [storedResponseButtonConfig, setStoredResponseButtonConfig] = useState<unknown>(null);
  const [isReplacingStoredResponseButtonConfig, setIsReplacingStoredResponseButtonConfig] = useState(false);

  // Logo/Favicon upload state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [faviconUploadError, setFaviconUploadError] = useState<string | null>(null);

  // Telegram token state (separate from form state for validation UI)
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramValidationStatus, setTelegramValidationStatus] = useState<TelegramValidationStatus>('idle');
  const [telegramUsername, setTelegramUsername] = useState<string | null>(null);
  const [telegramError, setTelegramError] = useState<string | null>(null);

  // Telegram webhook status state
  const [webhookInfo, setWebhookInfo] = useState<TelegramWebhookInfo | null>(null);
  const [webhookError, setWebhookError] = useState<string | null>(null);

  // WhatsApp credentials state (separate from form state for validation UI)
  const [whatsappAccessToken, setWhatsappAccessToken] = useState('');
  const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState('');
  const [whatsappWabaId, setWhatsappWabaId] = useState('');
  const [whatsappVerifyToken, setWhatsappVerifyToken] = useState('');
  const [whatsappValidationStatus, setWhatsappValidationStatus] = useState<WhatsAppValidationStatus>('idle');
  const [whatsappError, setWhatsappError] = useState<string | null>(null);

  // Stripe credentials state (separate from form state for validation UI)
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [stripeDefaultPriceId, setStripeDefaultPriceId] = useState('');
  const [stripeTestMode, setStripeTestMode] = useState(false);
  const [stripeValidationStatus, setStripeValidationStatus] = useState<StripeValidationStatus>('idle');
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);

  // Track if the form has been initialized from data
  const [isInitialized, setIsInitialized] = useState(false);

  // Tab state
  type TabId = 'general' | 'quick-response' | 'voice' | 'integrations' | 'appearance' | 'analytics' | 'plans';
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [saveSuccessTab, setSaveSuccessTab] = useState<TabId | null>(null);

  const showSaveSuccess = (tab: TabId) => {
    setSaveSuccessTab(tab);
    setTimeout(() => setSaveSuccessTab(null), 3000);
  };

  // Quick response test state
  const [quickResponseTestMessage, setQuickResponseTestMessage] = useState('');
  const [quickResponseTestResult, setQuickResponseTestResult] = useState<{
    response: string;
    model: string;
    usage: { promptTokens: number; completionTokens: number; totalTokens: number };
    latencyMs: number;
    estimatedCost: number;
    rawRequest: {
      systemPrompt: string;
      userMessage: string;
      model: string;
      maxTokens: number;
      temperature: number;
    };
    rawResponse: {
      content: string;
      model: string;
      finishReason: string;
      usage: { promptTokens: number; completionTokens: number; totalTokens: number };
    };
  } | null>(null);
  const [quickResponseTestError, setQuickResponseTestError] = useState<string | null>(null);
  const [showRawRequestResponse, setShowRawRequestResponse] = useState(false);

  // Prompt testing state - Summary prompts
  const [summaryTestDialog, setSummaryTestDialog] = useState<{
    isOpen: boolean;
    type: 'daily' | 'weekly';
    prompt: string;
    mockMessages: Array<{ role: 'user' | 'assistant'; content: string }>;
    previousSummary: string;
    isTesting: boolean;
    result: {
      summary: string;
      model: string;
      usage: { promptTokens: number; completionTokens: number; totalTokens: number };
      latencyMs: number;
    } | null;
    error: string | null;
    // User search state
    userSearchQuery: string;
    selectedUserId: string | null;
    selectedUserName: string | null;
    isLoadingUserMessages: boolean;
    // Time filter for conversation loading
    loadEntireConversation: boolean;
  }>({
    isOpen: false,
    type: 'daily',
    prompt: '',
    mockMessages: [{ role: 'user', content: '' }],
    previousSummary: '',
    isTesting: false,
    result: null,
    error: null,
    userSearchQuery: '',
    selectedUserId: null,
    selectedUserName: null,
    isLoadingUserMessages: false,
    loadEntireConversation: false,
  });

  // Prompt testing state - Analytics prompt
  const [analyticsTestDialog, setAnalyticsTestDialog] = useState<{
    isOpen: boolean;
    prompt: string;
    userMessage: string;
    trackGoalCompletion: boolean;
    isTesting: boolean;
    result: {
      analytics: Record<string, unknown>;
      rawResponse: string;
      model: string;
      usage: { promptTokens: number; completionTokens: number; totalTokens: number };
      latencyMs: number;
    } | null;
    error: string | null;
  }>({
    isOpen: false,
    prompt: '',
    userMessage: '',
    trackGoalCompletion: false,
    isTesting: false,
    result: null,
    error: null,
  });

  // Drag and drop state for UI blocks
  const [draggedBlockIndex, setDraggedBlockIndex] = useState<number | null>(null);
  const [dragOverBlockIndex, setDragOverBlockIndex] = useState<number | null>(null);

  // Plan modal state
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanData | null>(null);
  const [planSaveError, setPlanSaveError] = useState<string | undefined>(undefined);

  // Load bot data
  const botQuery = trpc.bots.get.useQuery(
    { id: botId! },
    { enabled: !!botId }
  );

  // Load available tools from the registry
  const toolsQuery = trpc.tools.list.useQuery();

  // User search query for loading real conversations in summary testing
  const userSearchQuery = trpc.users.list.useQuery(
    {
      search: summaryTestDialog.userSearchQuery,
      limit: 10,
      botId: botId ?? undefined,
    },
    {
      enabled: summaryTestDialog.isOpen && summaryTestDialog.userSearchQuery.length >= 2,
    }
  );

  // User message history query for loading real conversations
  // By default, filter by time period matching the summary type (daily = today, weekly = this_week)
  // unless loadEntireConversation is true
  const userMessageHistoryQuery = trpc.users.messageHistory.useQuery(
    {
      userId: summaryTestDialog.selectedUserId!,
      limit: 100,
      timeFilter: summaryTestDialog.loadEntireConversation
        ? 'all'
        : summaryTestDialog.type === 'daily'
          ? 'today'
          : 'this_week',
    },
    {
      enabled: summaryTestDialog.isOpen && !!summaryTestDialog.selectedUserId,
    }
  );

  // Per-tab update mutations
  // After each save, await the refetch and reset isInitialized so the form
  // re-syncs from the server data (ensures WhatsApp/Telegram state stays populated).
  const updateGeneralMutation = trpc.bots.update.useMutation({
    onSuccess: async () => {
      await utils.bots.get.invalidate({ id: botId! });
      utils.bots.list.invalidate();
      setIsInitialized(false);
      showSaveSuccess('general');
    },
  });

  const updateQuickResponseMutation = trpc.bots.update.useMutation({
    onSuccess: async () => {
      await utils.bots.get.invalidate({ id: botId! });
      setIsInitialized(false);
      showSaveSuccess('quick-response');
    },
  });

  const updateVoiceMutation = trpc.bots.update.useMutation({
    onSuccess: async () => {
      await utils.bots.get.invalidate({ id: botId! });
      setIsInitialized(false);
      showSaveSuccess('voice');
    },
  });

  const updateIntegrationsMutation = trpc.bots.update.useMutation({
    onSuccess: async () => {
      await utils.bots.get.invalidate({ id: botId! });
      setIsInitialized(false);
      showSaveSuccess('integrations');
    },
  });

  const updateAppearanceMutation = trpc.bots.update.useMutation({
    onSuccess: async () => {
      await utils.bots.get.invalidate({ id: botId! });
      setIsInitialized(false);
      showSaveSuccess('appearance');
    },
  });

  // Media upload mutation for logo/favicon
  const uploadMediaMutation = trpc.media.upload.useMutation();

  const updateAnalyticsMutation = trpc.bots.update.useMutation({
    onSuccess: async () => {
      await utils.bots.get.invalidate({ id: botId! });
      setIsInitialized(false);
      showSaveSuccess('analytics');
    },
  });

  // Telegram token validation mutation
  const validateTelegramMutation = trpc.bots.validateTelegramToken.useMutation({
    onSuccess: (data) => {
      setTelegramValidationStatus('success');
      setTelegramUsername(data.username);
      setTelegramError(null);
    },
    onError: (error) => {
      setTelegramValidationStatus('error');
      setTelegramUsername(null);
      setTelegramError(error.message || 'Invalid Telegram token');
    },
  });

  // Telegram webhook check mutation
  const checkWebhookMutation = trpc.bots.checkTelegramWebhook.useMutation({
    onSuccess: (data) => {
      setWebhookInfo(data);
      setWebhookError(null);
    },
    onError: (error) => {
      setWebhookInfo(null);
      setWebhookError(error.message || 'Failed to check webhook status');
    },
  });

  // WhatsApp credentials validation mutation
  const validateWhatsAppMutation = trpc.bots.validateWhatsAppCredentials.useMutation({
    onSuccess: () => {
      setWhatsappValidationStatus('success');
      setWhatsappError(null);
    },
    onError: (error) => {
      setWhatsappValidationStatus('error');
      setWhatsappError(error.message || 'Invalid WhatsApp credentials');
    },
  });

  // Stripe API key validation mutation
  const validateStripeMutation = trpc.integrations.validate.useMutation({
    onSuccess: (data) => {
      if (data.valid) {
        setStripeValidationStatus('success');
        setStripeAccountId(data.details?.accountId as string || null);
        setStripeError(null);
      } else {
        setStripeValidationStatus('error');
        setStripeAccountId(null);
        setStripeError(data.error || 'Invalid Stripe API key');
      }
    },
    onError: (error) => {
      setStripeValidationStatus('error');
      setStripeAccountId(null);
      setStripeError(error.message || 'Failed to validate Stripe API key');
    },
  });

  // Quick response test mutation
  const testQuickResponseMutation = trpc.bots.testQuickResponse.useMutation({
    onSuccess: (data) => {
      setQuickResponseTestResult(data);
      setQuickResponseTestError(null);
    },
    onError: (error) => {
      setQuickResponseTestResult(null);
      setQuickResponseTestError(error.message || 'Failed to test quick response');
    },
  });

  // Summary prompt test mutation
  const testSummaryPromptMutation = trpc.promptTesting.testSummaryPrompt.useMutation({
    onSuccess: (data) => {
      setSummaryTestDialog((prev) => ({
        ...prev,
        isTesting: false,
        result: {
          summary: data.summary,
          model: data.model,
          usage: data.usage,
          latencyMs: data.latencyMs,
        },
        error: null,
      }));
    },
    onError: (error) => {
      setSummaryTestDialog((prev) => ({
        ...prev,
        isTesting: false,
        result: null,
        error: error.message || 'Failed to test summary prompt',
      }));
    },
  });

  // Analytics prompt test mutation
  const testAnalyticsPromptMutation = trpc.promptTesting.testAnalyticsPrompt.useMutation({
    onSuccess: (data) => {
      setAnalyticsTestDialog((prev) => ({
        ...prev,
        isTesting: false,
        result: {
          analytics: data.analytics,
          rawResponse: data.rawResponse,
          model: data.model,
          usage: data.usage,
          latencyMs: data.latencyMs,
        },
        error: null,
      }));
    },
    onError: (error) => {
      setAnalyticsTestDialog((prev) => ({
        ...prev,
        isTesting: false,
        result: null,
        error: error.message || 'Failed to test analytics prompt',
      }));
    },
  });

  /**
   * Query to list subscription plans for the current bot.
   */
  const plansQuery = trpc.plans.list.useQuery(
    { botId: botId ?? '', limit: 50, offset: 0 },
    {
      enabled: !!botId,
      staleTime: 0,
      refetchOnMount: true,
      placeholderData: { plans: [], pagination: { total: 0, limit: 50, offset: 0, hasMore: false } },
    }
  );

  /**
   * Mutation to delete a subscription plan.
   */
  const deletePlanMutation = trpc.plans.delete.useMutation({
    onSuccess: () => {
      utils.plans.list.invalidate({ botId: botId ?? '' });
    },
  });

  /**
   * Mutation to create a new subscription plan.
   */
  const createPlanMutation = trpc.plans.create.useMutation({
    onSuccess: () => {
      utils.plans.list.invalidate({ botId: botId ?? '' });
      setIsPlanModalOpen(false);
      setEditingPlan(null);
      setPlanSaveError(undefined);
    },
    onError: (error) => {
      setPlanSaveError(error.message);
    },
  });

  /**
   * Mutation to update an existing subscription plan.
   */
  const updatePlanMutation = trpc.plans.update.useMutation({
    onSuccess: () => {
      utils.plans.list.invalidate({ botId: botId ?? '' });
      setIsPlanModalOpen(false);
      setEditingPlan(null);
      setPlanSaveError(undefined);
    },
    onError: (error) => {
      setPlanSaveError(error.message);
    },
  });

  // Initialize form state when bot data loads
  useEffect(() => {
    if (botQuery.data && !isInitialized) {
      const enabledTools = Array.isArray(botQuery.data.enabledTools)
        ? botQuery.data.enabledTools
        : [];
      const toolConfigs =
        botQuery.data.toolConfigs &&
        typeof botQuery.data.toolConfigs === 'object'
          ? (botQuery.data.toolConfigs as Record<string, Record<string, unknown>>)
          : {};
      // Initialize UI blocks from saved data or create defaults from available blocks
      let uiBlocks: UIBlock[] = [];
      if (botQuery.data.uiBlocks && Array.isArray(botQuery.data.uiBlocks)) {
        uiBlocks = (botQuery.data.uiBlocks as UIBlock[]).map((block) => ({
          ...block,
          visible: block.visible !== false, // Default to visible if not specified
        }));
      } else {
        // Create default UI blocks from available block types
        uiBlocks = AVAILABLE_UI_BLOCKS.map((block, index) => ({
          id: `block_${block.type}`,
          type: block.type,
          order: index,
          visible: true,
        }));
      }
      // Initialize integrations from saved data
      const integrations =
        botQuery.data.integrations &&
        typeof botQuery.data.integrations === 'object'
          ? (botQuery.data.integrations as Record<string, IntegrationConfig>)
          : {};

      // Initialize Telegram token state from existing integrations
      const telegramIntegration = integrations.telegram;
      if (telegramIntegration?.apiKey) {
        setTelegramToken(telegramIntegration.apiKey);
        if (telegramIntegration.verifiedAt && telegramIntegration.config?.username) {
          setTelegramValidationStatus('success');
          setTelegramUsername(telegramIntegration.config.username as string);
        }
      }

      // Initialize WhatsApp credentials state from existing integrations
      // Support both old format (apiKey, config.phoneNumberId) and new format (accessToken, phoneNumberId)
      const whatsappIntegration = integrations.whatsapp;
      const whatsappToken = whatsappIntegration?.accessToken || whatsappIntegration?.apiKey;
      if (whatsappToken) {
        setWhatsappAccessToken(whatsappToken);
        const phoneNumberId = whatsappIntegration?.phoneNumberId || whatsappIntegration?.config?.phoneNumberId;
        if (phoneNumberId) {
          setWhatsappPhoneNumberId(phoneNumberId as string);
        }
        if (whatsappIntegration?.config?.wabaId) {
          setWhatsappWabaId(whatsappIntegration.config.wabaId as string);
        }
        if (whatsappIntegration?.verifyToken) {
          setWhatsappVerifyToken(whatsappIntegration.verifyToken as string);
        }
        if (whatsappIntegration?.verifiedAt) {
          setWhatsappValidationStatus('success');
        }
      }

      // Initialize Stripe credentials state from existing integrations
      const stripeIntegration = integrations.stripe;
      if (stripeIntegration?.apiKey) {
        setStripeSecretKey(stripeIntegration.apiKey);
        if (stripeIntegration.config?.webhookSecret) {
          setStripeWebhookSecret(stripeIntegration.config.webhookSecret as string);
        }
        if (stripeIntegration.config?.priceId) {
          setStripeDefaultPriceId(stripeIntegration.config.priceId as string);
        }
        if (stripeIntegration.config?.testMode !== undefined) {
          setStripeTestMode(stripeIntegration.config.testMode as boolean);
        }
        if (stripeIntegration.verifiedAt) {
          setStripeValidationStatus('success');
          if (stripeIntegration.config?.accountId) {
            setStripeAccountId(stripeIntegration.config.accountId as string);
          }
        }
      }

      // Initialize voice config from saved data or use defaults
      const voiceConfig: VoiceConfig = botQuery.data.voiceConfig
        ? {
            enabled: (botQuery.data.voiceConfig as VoiceConfig).enabled ?? false,
            provider: (botQuery.data.voiceConfig as VoiceConfig).provider ?? 'openai',
            voiceId: (botQuery.data.voiceConfig as VoiceConfig).voiceId ?? 'alloy',
            speed: (botQuery.data.voiceConfig as VoiceConfig).speed ?? 1.0,
            pitch: (botQuery.data.voiceConfig as VoiceConfig).pitch,
            providerConfig: (botQuery.data.voiceConfig as VoiceConfig).providerConfig,
          }
        : {
            enabled: false,
            provider: 'openai',
            voiceId: 'alloy',
            speed: 1.0,
          };

      // Extract app branding fields
      const appBranding = botQuery.data.appBranding as {
        logoUrl?: string;
        faviconUrl?: string;
        headerFont?: string;
        bodyFont?: string;
        colorPalette?: {
          primary?: string;
          secondary?: string;
          background?: string;
          text?: string;
          accent?: string;
        };
        disableDesktopOnboarding?: boolean;
        externalOnboardingUrl?: string;
      } | null;

      const hydratedResponseButtonConfig = tryHydrateResponseButtonConfig(botQuery.data.responseButtonConfig);

      setFormState({
        name: botQuery.data.name || '',
        slug: botQuery.data.slug || '',
        customDomain: botQuery.data.customDomain || '',
        description: botQuery.data.description || '',
        systemPrompt: botQuery.data.systemPrompt || '',
        conversationInstructions: (botQuery.data as { conversationInstructions?: string | null }).conversationInstructions || '',
        voiceStyle: botQuery.data.voiceStyle || '',
        status: (botQuery.data.status as BotStatus) || 'active',
        overrideAiModel: botQuery.data.overrideAiModel || '',
        overrideAiMaxTokens: botQuery.data.overrideAiMaxTokens ?? null,
        overrideTrialDays: botQuery.data.overrideTrialDays ?? null,
        trialExpiredMessage: (botQuery.data as { trialExpiredMessage?: string | null }).trialExpiredMessage || '',
        subscriptionExpiredMessage: (botQuery.data as { subscriptionExpiredMessage?: string | null }).subscriptionExpiredMessage || '',
        messageLimitMessage: (botQuery.data as { messageLimitMessage?: string | null }).messageLimitMessage || '',
        enableScheduledMessages: botQuery.data.enableScheduledMessages ?? false,
        enabledTools,
        toolConfigs,
        uiBlocks,
        integrations,
        voiceConfig,
        quickResponseEnabled: botQuery.data.quickResponseEnabled ?? false,
        quickResponsePrompt: botQuery.data.quickResponsePrompt || '',
        quickResponseModel: botQuery.data.quickResponseModel || '',
        quickResponseTemperature: typeof botQuery.data.quickResponseTemperature === 'number'
          ? botQuery.data.quickResponseTemperature
          : (botQuery.data.quickResponseTemperature ? parseFloat(String(botQuery.data.quickResponseTemperature)) : 0.7),
        summaryModel: botQuery.data.summaryModel || '',
        typingDelayMs: (botQuery.data as { typingDelayMs?: number | null }).typingDelayMs ?? null,
        splitMessageDelay: (botQuery.data as { splitMessageDelay?: number | null }).splitMessageDelay ?? null,
        logoUrl: appBranding?.logoUrl || '',
        faviconUrl: appBranding?.faviconUrl || '',
        headerFont: appBranding?.headerFont || '',
        bodyFont: appBranding?.bodyFont || '',
        colorPalette: {
          primary: appBranding?.colorPalette?.primary || '',
          secondary: appBranding?.colorPalette?.secondary || '',
          background: appBranding?.colorPalette?.background || '',
          text: appBranding?.colorPalette?.text || '',
          accent: appBranding?.colorPalette?.accent || '',
        },
        disableDesktopOnboarding: appBranding?.disableDesktopOnboarding ?? false,
        externalOnboardingUrl: appBranding?.externalOnboardingUrl || '',
        cancelFlowStepsJson: botQuery.data.cancelFlowSteps
          ? JSON.stringify(botQuery.data.cancelFlowSteps, null, 2)
          : '',
        progressMilestonesJson: botQuery.data.progressMilestones
          ? JSON.stringify(botQuery.data.progressMilestones, null, 2)
          : '',
        responseButtonConfig: hydratedResponseButtonConfig.config,
      });
      setResponseButtonConfigError(null);
      setResponseButtonConfigParseError(hydratedResponseButtonConfig.error);
      setStoredResponseButtonConfig(hydratedResponseButtonConfig.success ? null : botQuery.data.responseButtonConfig);
      setIsReplacingStoredResponseButtonConfig(false);
      setIsInitialized(true);
    }
  }, [botQuery.data, isInitialized]);

  // Load user messages when a user is selected for summary testing
  useEffect(() => {
    if (
      userMessageHistoryQuery.data?.messages &&
      summaryTestDialog.isLoadingUserMessages &&
      summaryTestDialog.selectedUserId
    ) {
      // Transform the messages to the mock message format
      const messages = userMessageHistoryQuery.data.messages
        .map((msg) => ({
          role: (msg.direction === 'inbound' ? 'user' : 'assistant') as 'user' | 'assistant',
          content: msg.content,
        }))
        .reverse(); // Reverse to get chronological order (oldest first)

      setSummaryTestDialog((prev) => ({
        ...prev,
        mockMessages: messages.length > 0 ? messages : [{ role: 'user', content: '' }],
        isLoadingUserMessages: false,
      }));
    }
  }, [userMessageHistoryQuery.data, summaryTestDialog.isLoadingUserMessages, summaryTestDialog.selectedUserId]);

  /**
   * Handle form field changes.
   */
  const handleChange = <K extends keyof BotFormState>(
    field: K,
    value: BotFormState[K]
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Generate slug from name.
   */
  const generateSlug = (value: string) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  /**
   * Toggle a tool in the enabled tools list.
   */
  const handleToolToggle = (toolId: string, enabled: boolean) => {
    setFormState((prev) => {
      const newEnabledTools = enabled
        ? [...prev.enabledTools, toolId]
        : prev.enabledTools.filter((t) => t !== toolId);

      // If enabling a tool, initialize its config with defaults
      let newToolConfigs = { ...prev.toolConfigs };
      if (enabled && !newToolConfigs[toolId]) {
        // Find the tool in the API response
        const tool = toolsQuery.data?.tools.find((t: { id: string }) => t.id === toolId);
        const configSchema = tool?.configSchema as ToolConfigSchema | null;
        if (configSchema?.properties) {
          const defaultConfig: Record<string, unknown> = {};
          for (const [key, prop] of Object.entries(configSchema.properties)) {
            defaultConfig[key] = prop.default;
          }
          newToolConfigs[toolId] = defaultConfig;
        }
      }

      return {
        ...prev,
        enabledTools: newEnabledTools,
        toolConfigs: newToolConfigs,
      };
    });
  };

  /**
   * Update a tool configuration property.
   */
  const handleToolConfigChange = (
    toolId: string,
    property: string,
    value: boolean | number | string
  ) => {
    setFormState((prev) => ({
      ...prev,
      toolConfigs: {
        ...prev.toolConfigs,
        [toolId]: {
          ...prev.toolConfigs[toolId],
          [property]: value,
        },
      },
    }));
  };

  /**
   * Update a voice configuration property.
   */
  const handleVoiceConfigChange = <K extends keyof VoiceConfig>(
    field: K,
    value: VoiceConfig[K]
  ) => {
    setFormState((prev) => ({
      ...prev,
      voiceConfig: {
        ...prev.voiceConfig,
        [field]: value,
      },
    }));
  };

  /**
   * Toggle visibility of a UI block.
   */
  const handleToggleBlockVisibility = (index: number) => {
    setFormState((prev) => {
      const newBlocks = [...prev.uiBlocks];
      newBlocks[index] = { ...newBlocks[index], visible: !newBlocks[index].visible };
      return {
        ...prev,
        uiBlocks: newBlocks,
      };
    });
  };

  /**
   * Move a UI block up in the list.
   */
  const handleMoveBlockUp = (index: number) => {
    if (index === 0) return;
    setFormState((prev) => {
      const newBlocks = [...prev.uiBlocks];
      [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
      // Update order values
      newBlocks.forEach((block, i) => { block.order = i; });
      return {
        ...prev,
        uiBlocks: newBlocks,
      };
    });
  };

  /**
   * Move a UI block down in the list.
   */
  const handleMoveBlockDown = (index: number) => {
    setFormState((prev) => {
      if (index === prev.uiBlocks.length - 1) return prev;
      const newBlocks = [...prev.uiBlocks];
      [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
      // Update order values
      newBlocks.forEach((block, i) => { block.order = i; });
      return {
        ...prev,
        uiBlocks: newBlocks,
      };
    });
  };

  /**
   * Handle drag start for UI block reordering.
   */
  const handleBlockDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedBlockIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add visual feedback by setting opacity on the dragged element
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  /**
   * Handle drag end for UI block reordering.
   */
  const handleBlockDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setDraggedBlockIndex(null);
    setDragOverBlockIndex(null);
    // Reset opacity
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
  };

  /**
   * Handle drag over for UI block reordering.
   */
  const handleBlockDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedBlockIndex !== null && draggedBlockIndex !== index) {
      setDragOverBlockIndex(index);
    }
  };

  /**
   * Handle drag leave for UI block reordering.
   */
  const handleBlockDragLeave = () => {
    setDragOverBlockIndex(null);
  };

  /**
   * Handle drop for UI block reordering.
   */
  const handleBlockDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = draggedBlockIndex;

    if (sourceIndex === null || sourceIndex === targetIndex) {
      setDraggedBlockIndex(null);
      setDragOverBlockIndex(null);
      return;
    }

    setFormState((prev) => {
      const newBlocks = [...prev.uiBlocks];
      // Remove the dragged item and insert it at the target position
      const [draggedItem] = newBlocks.splice(sourceIndex, 1);
      newBlocks.splice(targetIndex, 0, draggedItem);
      // Update order values
      newBlocks.forEach((block, i) => { block.order = i; });
      return {
        ...prev,
        uiBlocks: newBlocks,
      };
    });

    setDraggedBlockIndex(null);
    setDragOverBlockIndex(null);
  };

  /**
   * Get the label and description for a UI block type.
   */
  const getBlockInfo = (type: string) => {
    const blockInfo = AVAILABLE_UI_BLOCKS.find((b) => b.type === type);
    return blockInfo || { label: type, description: '' };
  };

  /**
   * Handle Telegram token validation.
   */
  const handleValidateTelegramToken = () => {
    if (!telegramToken.trim()) {
      setTelegramError('Please enter a Telegram token');
      setTelegramValidationStatus('error');
      return;
    }
    setTelegramValidationStatus('validating');
    setTelegramError(null);
    validateTelegramMutation.mutate({ token: telegramToken.trim() });
  };

  /**
   * Handle Telegram token change.
   */
  const handleTelegramTokenChange = (value: string) => {
    setTelegramToken(value);
    // Reset validation state when token changes
    if (telegramValidationStatus !== 'idle') {
      setTelegramValidationStatus('idle');
      setTelegramUsername(null);
      setTelegramError(null);
    }
    // Reset webhook status when token changes
    setWebhookInfo(null);
    setWebhookError(null);
  };

  /**
   * Clear Telegram token configuration.
   */
  const handleClearTelegramToken = () => {
    setTelegramToken('');
    setTelegramValidationStatus('idle');
    setTelegramUsername(null);
    setTelegramError(null);
    // Reset webhook status
    setWebhookInfo(null);
    setWebhookError(null);
  };

  /**
   * Handle checking the Telegram webhook status.
   */
  const handleCheckWebhookStatus = () => {
    if (!telegramToken.trim()) {
      setWebhookError('Please enter a Telegram token');
      return;
    }
    setWebhookError(null);
    checkWebhookMutation.mutate({ token: telegramToken.trim() });
  };

  /**
   * Format a Unix timestamp to a readable date string.
   */
  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  /**
   * Handle WhatsApp credentials validation.
   */
  const handleValidateWhatsAppCredentials = () => {
    if (!whatsappAccessToken.trim()) {
      setWhatsappError('Please enter an Access Token');
      setWhatsappValidationStatus('error');
      return;
    }
    if (!whatsappPhoneNumberId.trim()) {
      setWhatsappError('Please enter a Phone Number ID');
      setWhatsappValidationStatus('error');
      return;
    }
    setWhatsappValidationStatus('validating');
    setWhatsappError(null);
    validateWhatsAppMutation.mutate({
      accessToken: whatsappAccessToken.trim(),
      phoneNumberId: whatsappPhoneNumberId.trim(),
    });
  };

  /**
   * Handle WhatsApp Access Token change.
   */
  const handleWhatsappAccessTokenChange = (value: string) => {
    setWhatsappAccessToken(value);
    // Reset validation state when credentials change
    if (whatsappValidationStatus !== 'idle') {
      setWhatsappValidationStatus('idle');
      setWhatsappError(null);
    }
  };

  /**
   * Handle WhatsApp Phone Number ID change.
   */
  const handleWhatsappPhoneNumberIdChange = (value: string) => {
    setWhatsappPhoneNumberId(value);
    // Reset validation state when credentials change
    if (whatsappValidationStatus !== 'idle') {
      setWhatsappValidationStatus('idle');
      setWhatsappError(null);
    }
  };

  /**
   * Handle WhatsApp WABA ID change.
   */
  const handleWhatsappWabaIdChange = (value: string) => {
    setWhatsappWabaId(value);
    // Reset validation state when credentials change
    if (whatsappValidationStatus !== 'idle') {
      setWhatsappValidationStatus('idle');
      setWhatsappError(null);
    }
  };

  /**
   * Clear WhatsApp credentials configuration.
   */
  const handleClearWhatsAppCredentials = () => {
    setWhatsappAccessToken('');
    setWhatsappPhoneNumberId('');
    setWhatsappWabaId('');
    setWhatsappValidationStatus('idle');
    setWhatsappError(null);
  };

  /**
   * Handle Stripe API key validation.
   */
  const handleValidateStripeCredentials = () => {
    if (!stripeSecretKey.trim()) {
      setStripeError('Please enter a Stripe Secret Key');
      setStripeValidationStatus('error');
      return;
    }
    setStripeValidationStatus('validating');
    setStripeError(null);
    validateStripeMutation.mutate({
      integrationType: 'stripe',
      apiKey: stripeSecretKey.trim(),
    });
  };

  /**
   * Handle Stripe Secret Key change.
   */
  const handleStripeSecretKeyChange = (value: string) => {
    setStripeSecretKey(value);
    // Reset validation state when credentials change
    if (stripeValidationStatus !== 'idle') {
      setStripeValidationStatus('idle');
      setStripeAccountId(null);
      setStripeError(null);
    }
  };

  /**
   * Handle Stripe Webhook Secret change.
   */
  const handleStripeWebhookSecretChange = (value: string) => {
    setStripeWebhookSecret(value);
  };

  /**
   * Handle Stripe Default Price ID change.
   */
  const handleStripeDefaultPriceIdChange = (value: string) => {
    setStripeDefaultPriceId(value);
  };

  /**
   * Handle Stripe Test Mode toggle.
   */
  const handleStripeTestModeChange = (value: boolean) => {
    setStripeTestMode(value);
  };

  /**
   * Clear Stripe credentials configuration.
   */
  const handleClearStripeCredentials = () => {
    setStripeSecretKey('');
    setStripeWebhookSecret('');
    setStripeDefaultPriceId('');
    setStripeTestMode(false);
    setStripeValidationStatus('idle');
    setStripeAccountId(null);
    setStripeError(null);
  };

  /**
   * Save General tab fields.
   */
  const handleSaveGeneral = () => {
    if (!botId) return;

    // Parse cancel flow steps JSON if provided
    let cancelFlowSteps = null;
    if (formState.cancelFlowStepsJson.trim()) {
      try {
        cancelFlowSteps = JSON.parse(formState.cancelFlowStepsJson);
      } catch {
        setCancelFlowStepsJsonError('Invalid JSON format');
        return;
      }
    }

    // Parse progress milestones JSON if provided
    let progressMilestones = null;
    if (formState.progressMilestonesJson.trim()) {
      try {
        progressMilestones = JSON.parse(formState.progressMilestonesJson);
      } catch {
        setProgressMilestonesJsonError('Invalid JSON format');
        return;
      }
    }

    let responseButtonConfigPayload: ResponseButtonConfig | null | undefined;

    if (responseButtonConfigParseError && !isReplacingStoredResponseButtonConfig) {
      responseButtonConfigPayload = undefined;
      setResponseButtonConfigError(null);
    } else {
      if (formState.responseButtonConfig == null) {
        responseButtonConfigPayload = botQuery.data?.responseButtonConfig == null ? undefined : null;
      } else {
        const responseButtonConfigResult = prepareResponseButtonConfigForSave(formState.responseButtonConfig, {
          preserveDefaultConfig: isReplacingStoredResponseButtonConfig,
        });
        if (!responseButtonConfigResult.success) {
          setResponseButtonConfigError(responseButtonConfigResult.error);
          return;
        }

        setResponseButtonConfigError(null);
        if (botQuery.data?.responseButtonConfig == null) {
          responseButtonConfigPayload = responseButtonConfigResult.config;
        } else if (responseButtonConfigResult.config) {
          responseButtonConfigPayload = responseButtonConfigResult.config;
        } else {
          responseButtonConfigPayload = null;
        }
      }
    }

    const updatePayload = {
      id: botId,
      name: formState.name,
      slug: formState.slug,
      customDomain: formState.customDomain || null,
      description: formState.description || null,
      systemPrompt: formState.systemPrompt,
      conversationInstructions: formState.conversationInstructions || null,
      voiceStyle: formState.voiceStyle || null,
      status: formState.status,
      overrideAiModel: formState.overrideAiModel || null,
      overrideAiMaxTokens: formState.overrideAiMaxTokens,
      overrideTrialDays: formState.overrideTrialDays,
      trialExpiredMessage: formState.trialExpiredMessage || null,
      subscriptionExpiredMessage: formState.subscriptionExpiredMessage || null,
      messageLimitMessage: formState.messageLimitMessage || null,
      typingDelayMs: formState.typingDelayMs,
      splitMessageDelay: formState.splitMessageDelay,
      enableScheduledMessages: formState.enableScheduledMessages,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cancelFlowSteps: cancelFlowSteps as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      progressMilestones: progressMilestones as any,
      ...(responseButtonConfigPayload !== undefined
        ? { responseButtonConfig: responseButtonConfigPayload }
        : {}),
    };

    updateGeneralMutation.mutate(updatePayload);
  };

  /**
   * Save Quick Response tab fields.
   */
  const handleSaveQuickResponse = () => {
    if (!botId) return;
    updateQuickResponseMutation.mutate({
      id: botId,
      quickResponseEnabled: formState.quickResponseEnabled,
      quickResponsePrompt: formState.quickResponsePrompt || null,
      quickResponseModel: formState.quickResponseModel || null,
      quickResponseTemperature: formState.quickResponseTemperature,
    });
  };

  /**
   * Save Voice tab fields.
   */
  const handleSaveVoice = () => {
    if (!botId) return;
    const voiceConfigToSave = formState.voiceConfig.enabled
      ? {
          enabled: true,
          provider: formState.voiceConfig.provider ?? 'openai',
          voiceId: formState.voiceConfig.voiceId ?? 'alloy',
          speed: formState.voiceConfig.speed ?? 1.0,
          providerConfig: formState.voiceConfig.providerConfig,
        }
      : { enabled: false };
    updateVoiceMutation.mutate({
      id: botId,
      voiceConfig: voiceConfigToSave,
    });
  };

  /**
   * Save Integrations tab fields.
   */
  const handleSaveIntegrations = () => {
    if (!botId) return;
    let integrationsToSave: Record<string, IntegrationConfig> | null = { ...formState.integrations };

    if (telegramToken.trim() && telegramValidationStatus === 'success' && telegramUsername) {
      integrationsToSave.telegram = {
        enabled: true,
        apiKey: telegramToken.trim(),
        verifiedAt: new Date().toISOString(),
        config: {
          username: telegramUsername,
        },
      };
    } else if (!telegramToken.trim() && integrationsToSave.telegram) {
      delete integrationsToSave.telegram;
    }

    if (whatsappAccessToken.trim() && whatsappPhoneNumberId.trim() && whatsappValidationStatus === 'success') {
      integrationsToSave.whatsapp = {
        enabled: true,
        accessToken: whatsappAccessToken.trim(),
        phoneNumberId: whatsappPhoneNumberId.trim(),
        verifyToken: whatsappVerifyToken.trim() || undefined,
        verifiedAt: new Date().toISOString(),
        config: whatsappWabaId.trim() ? { wabaId: whatsappWabaId.trim() } : undefined,
      };
    } else if (!whatsappAccessToken.trim() && !whatsappPhoneNumberId.trim() && integrationsToSave.whatsapp) {
      delete integrationsToSave.whatsapp;
    }

    // Handle Stripe integration
    if (stripeSecretKey.trim() && stripeValidationStatus === 'success') {
      integrationsToSave.stripe = {
        enabled: true,
        apiKey: stripeSecretKey.trim(),
        verifiedAt: new Date().toISOString(),
        config: {
          webhookSecret: stripeWebhookSecret.trim() || undefined,
          priceId: stripeDefaultPriceId.trim() || undefined,
          testMode: stripeTestMode,
          accountId: stripeAccountId || undefined,
        },
      };
    } else if (!stripeSecretKey.trim() && integrationsToSave.stripe) {
      delete integrationsToSave.stripe;
    }

    // Preserve unsupported legacy integrations until an explicit migration removes them.
    if (Object.keys(integrationsToSave).length === 0) {
      integrationsToSave = null;
    }

    updateIntegrationsMutation.mutate({
      id: botId,
      integrations: integrationsToSave,
    });
  };

  /**
   * Read a file and return its content as a Base64 string.
   */
  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handle logo file upload.
   */
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoUploadError('Please upload a valid image file (JPEG, PNG, GIF, WebP, or SVG)');
      return;
    }

    setIsUploadingLogo(true);
    setLogoUploadError(null);

    try {
      const base64Data = await readFileAsBase64(file);
      const result = await uploadMediaMutation.mutateAsync({
        filename: file.name,
        mimeType: file.type,
        base64Data,
        botId: botId ?? undefined,
      });
      // Set the uploaded URL to the form state
      handleChange('logoUrl', result.url);
    } catch (error) {
      setLogoUploadError(error instanceof Error ? error.message : 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      event.target.value = '';
    }
  };

  /**
   * Handle favicon file upload.
   */
  const handleFaviconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    if (!allowedTypes.includes(file.type)) {
      setFaviconUploadError('Please upload a valid image file (JPEG, PNG, GIF, WebP, SVG, or ICO)');
      return;
    }

    setIsUploadingFavicon(true);
    setFaviconUploadError(null);

    try {
      const base64Data = await readFileAsBase64(file);
      const result = await uploadMediaMutation.mutateAsync({
        filename: file.name,
        mimeType: file.type,
        base64Data,
        botId: botId ?? undefined,
      });
      // Set the uploaded URL to the form state
      handleChange('faviconUrl', result.url);
    } catch (error) {
      setFaviconUploadError(error instanceof Error ? error.message : 'Failed to upload favicon');
    } finally {
      setIsUploadingFavicon(false);
      // Reset file input
      event.target.value = '';
    }
  };

  /**
   * Handle color palette field changes.
   */
  const handleColorPaletteChange = (field: keyof ColorPalette, value: string) => {
    setFormState((prev) => ({
      ...prev,
      colorPalette: {
        ...prev.colorPalette,
        [field]: value,
      },
    }));
  };

  /**
   * Save Appearance & Tools tab fields.
   */
  const handleSaveAppearance = () => {
    if (!botId) return;
    const filteredToolConfigs: Record<string, Record<string, unknown>> = {};
    for (const toolId of formState.enabledTools) {
      if (formState.toolConfigs[toolId]) {
        filteredToolConfigs[toolId] = formState.toolConfigs[toolId];
      }
    }
    const uiBlocksToSave = formState.uiBlocks.length > 0
      ? formState.uiBlocks.map((block, index) => ({
          id: block.id,
          type: block.type,
          order: index,
          visible: block.visible,
        }))
      : null;

    // Build colorPalette only if at least one color is set
    const hasColorPalette = Object.values(formState.colorPalette).some((v) => v);
    const colorPaletteToSave = hasColorPalette
      ? {
          primary: formState.colorPalette.primary || undefined,
          secondary: formState.colorPalette.secondary || undefined,
          background: formState.colorPalette.background || undefined,
          text: formState.colorPalette.text || undefined,
          accent: formState.colorPalette.accent || undefined,
        }
      : undefined;

    // Build appBranding object only if at least one branding field is set
    const hasBranding =
      formState.logoUrl ||
      formState.faviconUrl ||
      formState.headerFont ||
      formState.bodyFont ||
      hasColorPalette ||
      formState.disableDesktopOnboarding ||
      formState.externalOnboardingUrl;

    const appBrandingToSave = hasBranding
      ? {
          logoUrl: formState.logoUrl || undefined,
          faviconUrl: formState.faviconUrl || undefined,
          headerFont: formState.headerFont || undefined,
          bodyFont: formState.bodyFont || undefined,
          colorPalette: colorPaletteToSave,
          disableDesktopOnboarding: formState.disableDesktopOnboarding || undefined,
          externalOnboardingUrl: formState.externalOnboardingUrl || undefined,
        }
      : null;
    updateAppearanceMutation.mutate({
      id: botId,
      uiBlocks: uiBlocksToSave,
      enabledTools: formState.enabledTools.length > 0 ? formState.enabledTools : null,
      toolConfigs: Object.keys(filteredToolConfigs).length > 0 ? filteredToolConfigs : null,
      appBranding: appBrandingToSave,
    });
  };

  /**
   * Save Analytics & Summaries tab fields.
   * Saves analytics and summaries configs into toolConfigs, plus summaryModel.
   */
  const handleSaveAnalytics = () => {
    if (!botId) return;

    // Build the toolConfigs with analytics and summaries configurations
    // The form fields are stored directly in toolConfigs.analytics and toolConfigs.summaries
    // They are being updated via handleToolConfigChange
    const updatedToolConfigs: Record<string, Record<string, unknown>> = {
      ...formState.toolConfigs,
    };

    updateAnalyticsMutation.mutate({
      id: botId,
      toolConfigs: Object.keys(updatedToolConfigs).length > 0 ? updatedToolConfigs : null,
      summaryModel: formState.summaryModel || null,
    });
  };

  // Handle context loading
  if (contextLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  // Handle no bot selected
  if (!botId) {
    return (
      <div style={styles.error}>
        No bot selected. Please select a bot from the sidebar.
      </div>
    );
  }

  // Loading state
  if (botQuery.isLoading) {
    return <div style={styles.loading}>Loading bot...</div>;
  }

  // Error state
  if (botQuery.error) {
    return (
      <div style={styles.error}>
        Error loading bot: {botQuery.error.message}
      </div>
    );
  }

  // Bot not found
  if (!botQuery.data) {
    return (
      <div style={styles.error}>
        Bot not found
      </div>
    );
  }

  // Tab definitions
  const tabs: { id: TabId; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'quick-response', label: 'Quick Response' },
    { id: 'voice', label: 'Voice' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'appearance', label: 'Appearance & Tools' },
    { id: 'analytics', label: 'Analytics & Summaries' },
    { id: 'plans', label: 'Plans' },
  ];

  /**
   * Helper to render per-tab save button with error/success feedback.
   */
  const renderTabActions = (
    tabId: TabId,
    mutation: { isPending: boolean; error: { message: string } | null },
    onSave: () => void,
    disabled?: boolean,
  ) => (
    <>
      {mutation.error && (
        <div style={styles.errorMessage}>
          Error saving: {mutation.error.message}
        </div>
      )}
      {saveSuccessTab === tabId && (
        <div style={styles.successMessage}>
          Changes saved successfully.
        </div>
      )}
      <div style={styles.tabActions}>
        <button
          type="button"
          onClick={onSave}
          disabled={mutation.isPending || disabled}
          style={{
            ...styles.submitButton,
            ...(mutation.isPending || disabled ? styles.buttonDisabled : {}),
          }}
        >
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </>
  );

  /**
   * Render the General tab content.
   */
  const renderGeneralTab = () => (
    <>
      {/* Basic Information Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Basic Information</h3>

        <div style={styles.fieldsGrid}>
          <div style={styles.field}>
            <label style={styles.label}>Name *</label>
            <input
              type="text"
              value={formState.name}
              onChange={(e) => {
                handleChange('name', e.target.value);
              }}
              style={styles.input}
              minLength={1}
              maxLength={100}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Slug *</label>
            <input
              type="text"
              value={formState.slug}
              onChange={(e) => handleChange('slug', generateSlug(e.target.value))}
              style={styles.input}
              minLength={1}
              maxLength={50}
              pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
              title="Lowercase letters, numbers, and dashes only"
            />
            <span style={styles.hint}>URL-friendly identifier (lowercase, dashes only)</span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Custom Domain / Hostname</label>
            <input
              type="text"
              value={formState.customDomain}
              onChange={(e) => handleChange('customDomain', e.target.value)}
              style={styles.input}
              placeholder="e.g., support.yourdomain.com"
              maxLength={255}
            />
            <span style={styles.hint}>
              Enter the hostname where this bot will be accessed (e.g., support.yourdomain.com).
              Must be unique. Configure your DNS to point this hostname to the application server.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Status</label>
            <select
              value={formState.status}
              onChange={(e) => handleChange('status', e.target.value as BotStatus)}
              style={styles.select}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Description</label>
          <textarea
            value={formState.description}
            onChange={(e) => handleChange('description', e.target.value)}
            style={styles.textarea}
            rows={2}
            placeholder="Brief description of the bot's purpose"
          />
        </div>
      </div>

      {/* Personality Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Personality & Behavior</h3>

        <div style={styles.field}>
          <label style={styles.label}>System Prompt *</label>
          <textarea
            value={formState.systemPrompt}
            onChange={(e) => handleChange('systemPrompt', e.target.value)}
            style={styles.textarea}
            rows={8}
            minLength={1}
            placeholder="Define the bot's personality, role, and behavior guidelines"
          />
          <span style={styles.hint}>
            This prompt defines how the bot interacts with users
          </span>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Voice Style</label>
          <textarea
            value={formState.voiceStyle}
            onChange={(e) => handleChange('voiceStyle', e.target.value)}
            style={styles.textarea}
            rows={4}
            placeholder="e.g., warm, professional, casual, friendly"
          />
          <span style={styles.hint}>
            Describes the tone and style of the bot's responses
          </span>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Conversation Instructions</label>
          <textarea
            value={formState.conversationInstructions || DEFAULT_CONVERSATION_INSTRUCTIONS}
            onChange={(e) => handleChange('conversationInstructions', e.target.value)}
            style={styles.textarea}
            rows={12}
          />
          <span style={styles.hint}>
            Instructions for how the bot should behave in conversations. Edit to customize.
          </span>
        </div>

      </div>

      {/* Advanced Settings Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Advanced Settings</h3>
        <p style={styles.sectionDescription}>
          Optional overrides for AI model, token limits, and trial period.
        </p>

        <div style={styles.fieldsGrid}>
          <div style={styles.field}>
            <label style={styles.label}>AI Model Override</label>
            <input
              type="text"
              value={formState.overrideAiModel}
              onChange={(e) => handleChange('overrideAiModel', e.target.value)}
              style={styles.input}
              placeholder="e.g., google/gemini-3-flash-preview"
            />
            <span style={styles.hint}>
              OpenRouter agent ID format (e.g., google/gemini-3-flash-preview). Leave empty to use system default.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Max Tokens Override</label>
            <input
              type="number"
              value={formState.overrideAiMaxTokens ?? ''}
              onChange={(e) =>
                handleChange(
                  'overrideAiMaxTokens',
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              style={styles.input}
              placeholder="e.g., 4096"
              min={1}
            />
            <span style={styles.hint}>
              Maximum tokens for AI responses
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Trial Days Override</label>
            <input
              type="number"
              value={formState.overrideTrialDays ?? ''}
              onChange={(e) =>
                handleChange(
                  'overrideTrialDays',
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              style={styles.input}
              placeholder="e.g., 14"
              min={1}
            />
            <span style={styles.hint}>
              Custom trial period for this bot
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Trial Expired Message</label>
            <textarea
              value={formState.trialExpiredMessage}
              onChange={(e) => handleChange('trialExpiredMessage', e.target.value)}
              style={styles.textarea}
              rows={4}
              maxLength={1000}
              placeholder="Leave empty to use the default trial expired message..."
            />
            <span style={styles.hint}>
              Sent when a trial user no longer has access and needs to subscribe.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Subscription Expired Message</label>
            <textarea
              value={formState.subscriptionExpiredMessage}
              onChange={(e) => handleChange('subscriptionExpiredMessage', e.target.value)}
              style={styles.textarea}
              rows={4}
              maxLength={1000}
              placeholder="Leave empty to use the default subscription expired message..."
            />
            <span style={styles.hint}>
              Sent when a paid subscription has expired or been cancelled.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Message Limit Reached Message</label>
            <textarea
              value={formState.messageLimitMessage}
              onChange={(e) => handleChange('messageLimitMessage', e.target.value)}
              style={styles.textarea}
              rows={4}
              maxLength={1000}
              placeholder="Leave empty to use the default message limit response..."
            />
            <span style={styles.hint}>
              Sent when a user hits their daily message allowance.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>WhatsApp-Only Typing Delay (ms)</label>
            <input
              type="number"
              value={formState.typingDelayMs ?? ''}
              onChange={(e) =>
                handleChange(
                  'typingDelayMs',
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              style={styles.input}
              placeholder="e.g., 1200"
              min={0}
            />
            <span style={styles.hint}>
              Only affects WhatsApp read receipts and typing indicators. Other channels stay immediate. Leave empty or 0 to keep WhatsApp immediate too.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Extra Split Message Delay (ms)</label>
            <input
              type="number"
              value={formState.splitMessageDelay ?? ''}
              onChange={(e) =>
                handleChange(
                  'splitMessageDelay',
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              style={styles.input}
              placeholder="e.g., 12000"
            />
            <span style={styles.hint}>
              Additional delay added to the default ~3s between split messages. Can be negative to speed up. Leave empty for default only.
            </span>
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={formState.enableScheduledMessages}
              onChange={(e) => handleChange('enableScheduledMessages', e.target.checked)}
              style={styles.checkbox}
            />
            <span style={styles.checkboxText}>Enable Scheduled Messages</span>
          </label>
          <span style={styles.hint}>
            Allow the AI to schedule follow-up messages to be sent at specific times.
            When enabled, the bot can proactively reach out to users based on conversation context.
          </span>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Cancel Flow Steps (JSON)</label>
          <textarea
            value={formState.cancelFlowStepsJson}
            onChange={(e) => {
              handleChange('cancelFlowStepsJson', e.target.value);
              // Validate JSON on change
              if (e.target.value.trim()) {
                try {
                  JSON.parse(e.target.value);
                  setCancelFlowStepsJsonError(null);
                } catch {
                  setCancelFlowStepsJsonError('Invalid JSON format');
                }
              } else {
                setCancelFlowStepsJsonError(null);
              }
            }}
            style={{
              ...styles.textarea,
              ...(cancelFlowStepsJsonError ? { borderColor: '#ef4444' } : {}),
              fontFamily: 'monospace',
              fontSize: '12px',
            }}
            rows={8}
            placeholder='[{"id": "step1", "type": "emotional_appeal", "order": 0, "message": "We would miss you!"}]'
          />
          {cancelFlowStepsJsonError && (
            <span style={{ ...styles.hint, color: '#ef4444' }}>{cancelFlowStepsJsonError}</span>
          )}
          <span style={styles.hint}>
            Configure the cancellation retention flow steps. Each step can include emotional appeals,
            pause offers, discounts, reason selection, or custom content.
          </span>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Progress Milestones (JSON)</label>
          <textarea
            value={formState.progressMilestonesJson}
            onChange={(e) => {
              handleChange('progressMilestonesJson', e.target.value);
              // Validate JSON on change
              if (e.target.value.trim()) {
                try {
                  JSON.parse(e.target.value);
                  setProgressMilestonesJsonError(null);
                } catch {
                  setProgressMilestonesJsonError('Invalid JSON format');
                }
              } else {
                setProgressMilestonesJsonError(null);
              }
            }}
            style={{
              ...styles.textarea,
              ...(progressMilestonesJsonError ? { borderColor: '#ef4444' } : {}),
              fontFamily: 'monospace',
              fontSize: '12px',
            }}
            rows={8}
            placeholder='[{"id": "milestone1", "name": "First Week", "order": 0, "criteria": {"type": "days_active", "target": 7}}]'
          />
          {progressMilestonesJsonError && (
            <span style={{ ...styles.hint, color: '#ef4444' }}>{progressMilestonesJsonError}</span>
          )}
          <span style={styles.hint}>
            Define milestone-based journeys for the progress tracker. Each milestone can track days active,
            check-ins completed, messages sent, or custom fields.
          </span>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Response Buttons</h3>
        <p style={styles.sectionDescription}>
          Configure WhatsApp reply buttons per message surface. {RESPONSE_BUTTON_ROLLOUT_GUIDANCE}
        </p>
        {responseButtonConfigParseError && !isReplacingStoredResponseButtonConfig ? (
          <div style={styles.warningBox}>
            <p style={styles.warningText}>
              The saved response button configuration could not be parsed by this admin build. Saving this tab will leave the stored JSON unchanged until you explicitly replace it.
            </p>
            <pre style={styles.jsonPreview}>
              {JSON.stringify(storedResponseButtonConfig, null, 2)}
            </pre>
            <button
              type="button"
              onClick={() => {
                setResponseButtonConfigParseError(null);
                setStoredResponseButtonConfig(null);
                setIsReplacingStoredResponseButtonConfig(true);
                setResponseButtonConfigError(null);
                handleChange('responseButtonConfig', createEmptyResponseButtonConfig());
              }}
              style={styles.warningActionButton}
            >
              Replace with new editable config
            </button>
          </div>
        ) : (
          <ResponseButtonConfigEditor
            value={formState.responseButtonConfig ?? createEmptyResponseButtonConfig()}
            onChange={(value) => {
              setResponseButtonConfigError(null);
              handleChange('responseButtonConfig', value);
            }}
            error={responseButtonConfigError}
          />
        )}
      </div>

      {renderTabActions(
        'general',
        updateGeneralMutation,
        handleSaveGeneral,
        !formState.name
          || !formState.slug
          || !formState.systemPrompt
          || !!cancelFlowStepsJsonError
          || !!progressMilestonesJsonError,
      )}
    </>
  );

  /**
   * Render the Quick Response tab content.
   */
  const renderQuickResponseTab = () => (
    <>
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Quick Response</h3>
        <p style={styles.sectionDescription}>
          Configure quick response settings for faster AI replies.
          When enabled, the bot uses a lightweight AI model to provide instant acknowledgements.
        </p>

        <div style={styles.quickResponseSection}>
          <div style={styles.field}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formState.quickResponseEnabled}
                onChange={(e) => handleChange('quickResponseEnabled', e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>Enable Quick Response</span>
            </label>
            <span style={styles.hint}>
              When enabled, the bot can send quick acknowledgements before the full AI response.
            </span>
          </div>

          {formState.quickResponseEnabled && (
            <div style={styles.quickResponseFields}>
              <div style={styles.field}>
                <label style={styles.label}>Quick Response Model (Optional)</label>
                <input
                  type="text"
                  value={formState.quickResponseModel}
                  onChange={(e) => handleChange('quickResponseModel', e.target.value)}
                  style={styles.input}
                  placeholder="Default: google/gemini-2.5-flash-lite"
                />
                <span style={styles.hint}>
                  OpenRouter model ID for quick responses. Leave empty to use default (google/gemini-2.5-flash-lite).
                </span>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  Quick Response Temperature: {formState.quickResponseTemperature.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={formState.quickResponseTemperature}
                  onChange={(e) => handleChange('quickResponseTemperature', parseFloat(e.target.value))}
                  style={{ ...styles.input, cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                  <span>0 - Focused</span>
                  <span>1 - Balanced</span>
                  <span>2 - Creative</span>
                </div>
                <span style={styles.hint}>
                  Controls the creativity of quick responses. Lower values (0-0.5) produce more focused, predictable responses. Higher values (1.5-2) produce more creative, varied responses. Default is 0.7.
                </span>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Quick Response Prompt (Optional)</label>
                <textarea
                  value={formState.quickResponsePrompt}
                  onChange={(e) => handleChange('quickResponsePrompt', e.target.value)}
                  style={styles.textarea}
                  rows={4}
                  placeholder="Leave empty to use system default prompt..."
                />
                <span style={styles.hint}>
                  Custom prompt for the quick response AI. Leave empty to use system default.
                </span>
              </div>

              {/* Quick Response Test Section */}
              <div style={styles.quickResponseTestSection}>
                <label style={styles.label}>Test Quick Response</label>
                <div style={styles.quickResponseTestInputRow}>
                  <input
                    type="text"
                    value={quickResponseTestMessage}
                    onChange={(e) => setQuickResponseTestMessage(e.target.value)}
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="Enter a test message to simulate user input..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (quickResponseTestMessage) {
                        setQuickResponseTestResult(null);
                        setQuickResponseTestError(null);
                        testQuickResponseMutation.mutate({
                          quickResponsePrompt: formState.quickResponsePrompt || null,
                          quickResponseModel: formState.quickResponseModel || null,
                          quickResponseTemperature: formState.quickResponseTemperature,
                          testMessage: quickResponseTestMessage,
                        });
                      }
                    }}
                    disabled={!quickResponseTestMessage || testQuickResponseMutation.isPending}
                    style={{
                      ...styles.verifyButton,
                      ...(!quickResponseTestMessage || testQuickResponseMutation.isPending ? styles.buttonDisabled : {}),
                    }}
                  >
                    {testQuickResponseMutation.isPending ? 'Testing...' : 'Test'}
                  </button>
                </div>
                <span style={styles.hint}>
                  Simulate a user message to see how the quick response will be generated.
                </span>

                {/* Test Results */}
                {quickResponseTestResult && (
                  <div style={styles.quickResponseTestResults}>
                    <div style={styles.quickResponseTestResultHeader}>
                      <span style={styles.successIcon}>✓</span>
                      <span>Quick Response Generated</span>
                    </div>
                    <div style={styles.quickResponseTestResponse}>
                      "{quickResponseTestResult.response}"
                    </div>
                    <div style={styles.quickResponseTestMetrics}>
                      <div style={styles.quickResponseTestMetricItem}>
                        <span style={styles.quickResponseTestMetricLabel}>Execution Time</span>
                        <span style={styles.quickResponseTestMetricValue}>{quickResponseTestResult.latencyMs}ms</span>
                      </div>
                      <div style={styles.quickResponseTestMetricItem}>
                        <span style={styles.quickResponseTestMetricLabel}>Model</span>
                        <span style={styles.quickResponseTestMetricValue}>{quickResponseTestResult.model}</span>
                      </div>
                      <div style={styles.quickResponseTestMetricItem}>
                        <span style={styles.quickResponseTestMetricLabel}>Tokens</span>
                        <span style={styles.quickResponseTestMetricValue}>
                          {quickResponseTestResult.usage.promptTokens} in / {quickResponseTestResult.usage.completionTokens} out
                        </span>
                      </div>
                      <div style={styles.quickResponseTestMetricItem}>
                        <span style={styles.quickResponseTestMetricLabel}>Est. Cost</span>
                        <span style={styles.quickResponseTestMetricValue}>
                          ${quickResponseTestResult.estimatedCost.toFixed(6)}
                        </span>
                      </div>
                    </div>

                    {/* Raw Request/Response Toggle */}
                    <button
                      type="button"
                      onClick={() => setShowRawRequestResponse(!showRawRequestResponse)}
                      style={styles.rawToggleButton}
                    >
                      {showRawRequestResponse ? 'Hide' : 'Show'} Raw Request/Response
                    </button>

                    {showRawRequestResponse && (
                      <div style={styles.rawDataSection}>
                        {/* Raw Request */}
                        <div style={styles.rawDataBlock}>
                          <div style={styles.rawDataHeader}>Raw Request</div>
                          <pre style={styles.rawDataContent}>
{JSON.stringify({
  systemPrompt: quickResponseTestResult.rawRequest.systemPrompt,
  userMessage: quickResponseTestResult.rawRequest.userMessage,
  model: quickResponseTestResult.rawRequest.model,
  maxTokens: quickResponseTestResult.rawRequest.maxTokens,
  temperature: quickResponseTestResult.rawRequest.temperature,
}, null, 2)}
                          </pre>
                        </div>

                        {/* Raw Response */}
                        <div style={styles.rawDataBlock}>
                          <div style={styles.rawDataHeader}>Raw Response</div>
                          <pre style={styles.rawDataContent}>
{JSON.stringify({
  content: quickResponseTestResult.rawResponse.content,
  model: quickResponseTestResult.rawResponse.model,
  finishReason: quickResponseTestResult.rawResponse.finishReason,
  usage: quickResponseTestResult.rawResponse.usage,
}, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Test Error */}
                {quickResponseTestError && (
                  <div style={styles.telegramError}>
                    <span style={styles.errorIcon}>✕</span>
                    <span>{quickResponseTestError}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {renderTabActions('quick-response', updateQuickResponseMutation, handleSaveQuickResponse)}
    </>
  );

  /**
   * Render the Voice tab content.
   */
  const renderVoiceTab = () => (
    <>
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Voice Output</h3>
        <p style={styles.sectionDescription}>
          Configure text-to-speech settings for voice responses.
          When enabled, the bot can respond with audio messages using OpenAI's TTS.
        </p>

        <div style={styles.voiceConfigSection}>
          <div style={styles.field}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formState.voiceConfig.enabled}
                onChange={(e) => handleVoiceConfigChange('enabled', e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>Enable Voice Output</span>
            </label>
            <span style={styles.hint}>
              When enabled, the bot will send audio responses in addition to text.
            </span>
          </div>

          {formState.voiceConfig.enabled && (
            <div style={styles.voiceConfigFields}>
              <div style={styles.fieldsGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>TTS Model</label>
                  <select
                    value={formState.voiceConfig.providerConfig?.model as string ?? 'tts-1'}
                    onChange={(e) => handleVoiceConfigChange('providerConfig', {
                      ...formState.voiceConfig.providerConfig,
                      model: e.target.value,
                    })}
                    style={styles.select}
                  >
                    {TTS_MODELS.map((model) => (
                      <option key={model.value} value={model.value}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <span style={styles.hint}>
                    TTS-1 is faster, TTS-1-HD has higher audio quality.
                  </span>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Voice</label>
                  <select
                    value={formState.voiceConfig.voiceId ?? 'alloy'}
                    onChange={(e) => handleVoiceConfigChange('voiceId', e.target.value)}
                    style={styles.select}
                  >
                    {TTS_VOICES.map((voice) => (
                      <option key={voice.value} value={voice.value}>
                        {voice.label} - {voice.description}
                      </option>
                    ))}
                  </select>
                  <span style={styles.hint}>
                    Choose the voice personality for audio responses.
                  </span>
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Speed</label>
                <div style={styles.speedInputContainer}>
                  <input
                    type="range"
                    min="0.25"
                    max="4.0"
                    step="0.05"
                    value={formState.voiceConfig.speed ?? 1.0}
                    onChange={(e) => handleVoiceConfigChange('speed', parseFloat(e.target.value))}
                    style={styles.speedSlider}
                  />
                  <input
                    type="number"
                    min="0.25"
                    max="4.0"
                    step="0.05"
                    value={formState.voiceConfig.speed ?? 1.0}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (value >= 0.25 && value <= 4.0) {
                        handleVoiceConfigChange('speed', value);
                      }
                    }}
                    style={styles.speedInput}
                  />
                </div>
                <span style={styles.hint}>
                  Speaking rate: 0.25 (slowest) to 4.0 (fastest). Default is 1.0.
                </span>
              </div>

              <div style={styles.voicePreviewInfo}>
                <div style={styles.voicePreviewLabel}>Voice Preview:</div>
                <div style={styles.voicePreviewText}>
                  <strong>{TTS_VOICES.find(v => v.value === formState.voiceConfig.voiceId)?.label ?? 'Alloy'}</strong>
                  {' - '}
                  {TTS_VOICES.find(v => v.value === formState.voiceConfig.voiceId)?.description ?? 'Neutral and balanced'}
                  {' at '}
                  {formState.voiceConfig.speed ?? 1.0}x speed
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {renderTabActions('voice', updateVoiceMutation, handleSaveVoice)}
    </>
  );

  /**
   * Render the Integrations tab content.
   */
  const unsupportedIntegrationKeys = Object.keys(formState.integrations).filter(
    (key) => !SUPPORTED_INTEGRATION_KEYS.includes(key as typeof SUPPORTED_INTEGRATION_KEYS[number])
  );

  const renderIntegrationsTab = () => (
    <>
      {unsupportedIntegrationKeys.length > 0 && (
        <div style={styles.paymentHint}>
          Legacy integrations hidden from this form will be preserved on save:
          {' '}
          {unsupportedIntegrationKeys.join(', ')}.
        </div>
      )}

      {/* Telegram Integration Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Telegram Integration</h3>
        <p style={styles.sectionDescription}>
          Connect a Telegram bot to enable messaging through Telegram.
          Get your bot token from @BotFather on Telegram.
        </p>

        <div style={styles.telegramSection}>
          <div style={styles.field}>
            <label style={styles.label}>Telegram Bot Token</label>
            <div style={styles.telegramInputRow}>
              <input
                type="text"
                value={telegramToken}
                onChange={(e) => handleTelegramTokenChange(e.target.value)}
                style={{
                  ...styles.input,
                  ...styles.telegramInput,
                  ...(telegramValidationStatus === 'success' ? styles.inputSuccess : {}),
                  ...(telegramValidationStatus === 'error' ? styles.inputError : {}),
                }}
                placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
              />
              <button
                type="button"
                onClick={handleValidateTelegramToken}
                disabled={telegramValidationStatus === 'validating' || !telegramToken.trim()}
                style={{
                  ...styles.verifyButton,
                  ...(telegramValidationStatus === 'validating' || !telegramToken.trim() ? styles.buttonDisabled : {}),
                }}
              >
                {telegramValidationStatus === 'validating' ? 'Verifying...' : 'Verify'}
              </button>
              {telegramToken && (
                <button
                  type="button"
                  onClick={handleClearTelegramToken}
                  style={styles.clearButton}
                  title="Clear token"
                >
                  ×
                </button>
              )}
            </div>
            <span style={styles.hint}>
              Create a bot with @BotFather on Telegram and paste the token here.
            </span>
          </div>

          {/* Validation Status Display */}
          {telegramValidationStatus === 'success' && telegramUsername && (
            <div style={styles.telegramSuccess}>
              <span style={styles.successIcon}>✓</span>
              <span>
                Connected to <strong>@{telegramUsername}</strong>
              </span>
            </div>
          )}

          {telegramValidationStatus === 'error' && telegramError && (
            <div style={styles.telegramError}>
              <span style={styles.errorIcon}>✗</span>
              <span>{telegramError}</span>
            </div>
          )}

          {telegramValidationStatus === 'idle' && telegramToken && (
            <div style={styles.telegramHint}>
              Click &quot;Verify&quot; to validate your token before saving.
            </div>
          )}

          {/* Webhook Status Panel */}
          {telegramValidationStatus === 'success' && telegramToken && (
            <div style={styles.webhookStatusPanel}>
              <div style={styles.webhookStatusHeader}>
                <h4 style={styles.webhookStatusTitle}>Webhook Status</h4>
                <button
                  type="button"
                  onClick={handleCheckWebhookStatus}
                  disabled={checkWebhookMutation.isPending}
                  style={{
                    ...styles.checkWebhookButton,
                    ...(checkWebhookMutation.isPending ? styles.buttonDisabled : {}),
                  }}
                >
                  {checkWebhookMutation.isPending ? 'Checking...' : 'Check Webhook'}
                </button>
              </div>

              {webhookError && (
                <div style={styles.webhookError}>
                  <span style={styles.errorIcon}>✗</span>
                  <span>{webhookError}</span>
                </div>
              )}

              {webhookInfo && (
                <div style={styles.webhookInfoContainer}>
                  <div style={styles.webhookInfoRow}>
                    <span style={styles.webhookInfoLabel}>Webhook URL:</span>
                    <span style={styles.webhookInfoValue}>
                      {webhookInfo.url || <em style={styles.webhookNoValue}>Not configured</em>}
                    </span>
                  </div>
                  <div style={styles.webhookInfoRow}>
                    <span style={styles.webhookInfoLabel}>Pending Updates:</span>
                    <span style={{
                      ...styles.webhookInfoValue,
                      ...(webhookInfo.pendingUpdateCount > 0 ? styles.webhookWarningValue : {}),
                    }}>
                      {webhookInfo.pendingUpdateCount}
                    </span>
                  </div>
                  {webhookInfo.ipAddress && (
                    <div style={styles.webhookInfoRow}>
                      <span style={styles.webhookInfoLabel}>IP Address:</span>
                      <span style={styles.webhookInfoValue}>{webhookInfo.ipAddress}</span>
                    </div>
                  )}
                  {webhookInfo.lastErrorMessage && (
                    <div style={styles.webhookErrorInfo}>
                      <div style={styles.webhookInfoRow}>
                        <span style={styles.webhookInfoLabel}>Last Error:</span>
                        <span style={styles.webhookErrorValue}>{webhookInfo.lastErrorMessage}</span>
                      </div>
                      {webhookInfo.lastErrorDate && (
                        <div style={styles.webhookInfoRow}>
                          <span style={styles.webhookInfoLabel}>Error Date:</span>
                          <span style={styles.webhookInfoValue}>
                            {formatTimestamp(webhookInfo.lastErrorDate)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {webhookInfo.maxConnections && (
                    <div style={styles.webhookInfoRow}>
                      <span style={styles.webhookInfoLabel}>Max Connections:</span>
                      <span style={styles.webhookInfoValue}>{webhookInfo.maxConnections}</span>
                    </div>
                  )}
                </div>
              )}

              {!webhookInfo && !webhookError && !checkWebhookMutation.isPending && (
                <div style={styles.webhookHint}>
                  Click &quot;Check Webhook&quot; to see the current webhook status.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Integration Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>WhatsApp Integration</h3>
        <p style={styles.sectionDescription}>
          Connect a WhatsApp Business account to enable messaging through WhatsApp.
          Get your credentials from the Meta Business Suite / WhatsApp Business Platform.
        </p>

        <div style={styles.whatsappSection}>
          <div style={styles.field}>
            <label style={styles.label}>Access Token (API Key)</label>
            <div style={styles.whatsappInputRow}>
              <input
                type="password"
                value={whatsappAccessToken}
                onChange={(e) => handleWhatsappAccessTokenChange(e.target.value)}
                style={{
                  ...styles.input,
                  ...styles.whatsappInput,
                  ...(whatsappValidationStatus === 'success' ? styles.inputSuccess : {}),
                  ...(whatsappValidationStatus === 'error' ? styles.inputError : {}),
                }}
                placeholder="EAAxxxxxxxxxxxxxx..."
              />
            </div>
            <span style={styles.hint}>
              Your Meta Graph API access token for WhatsApp Cloud API.{' '}
              <a
                href="https://business.facebook.com/latest/settings/system_users"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.hintLink}
              >
                Configure access token in Meta Business Dashboard
              </a>
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Phone Number ID</label>
            <input
              type="text"
              value={whatsappPhoneNumberId}
              onChange={(e) => handleWhatsappPhoneNumberIdChange(e.target.value)}
              style={{
                ...styles.input,
                ...(whatsappValidationStatus === 'success' ? styles.inputSuccess : {}),
                ...(whatsappValidationStatus === 'error' ? styles.inputError : {}),
              }}
              placeholder="123456789012345"
            />
            <span style={styles.hint}>
              The Phone Number ID from your WhatsApp Business account.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>WhatsApp Business Account ID (WABA ID) - Optional</label>
            <input
              type="text"
              value={whatsappWabaId}
              onChange={(e) => handleWhatsappWabaIdChange(e.target.value)}
              style={styles.input}
              placeholder="123456789012345"
            />
            <span style={styles.hint}>
              The WhatsApp Business Account ID (optional, for advanced features).
            </span>
          </div>

          {/* Webhook Configuration Section */}
          <div style={styles.webhookConfigSection}>
            <h4 style={styles.webhookConfigTitle}>Webhook Configuration</h4>
            <p style={styles.webhookConfigDescription}>
              Enter these values in the{' '}
              <a
                href="https://developers.facebook.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.hintLink}
              >
                Meta App Dashboard
              </a>
              {' '}under WhatsApp {'>'} Configuration {'>'} Webhook.
            </p>

            <div style={styles.field}>
              <label style={styles.label}>Callback URL (Webhook URL)</label>
              <div style={styles.webhookUrlContainer}>
                <input
                  type="text"
                  value={botId ? `${window.location.origin}/webhooks/whatsapp/${botId}` : ''}
                  readOnly
                  style={{ ...styles.input, ...styles.readOnlyInput }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (botId) {
                      navigator.clipboard.writeText(`${window.location.origin}/webhooks/whatsapp/${botId}`);
                    }
                  }}
                  style={styles.copyButton}
                  title="Copy to clipboard"
                >
                  Copy
                </button>
              </div>
              <span style={styles.hint}>
                Enter this URL as the Callback URL in Meta&apos;s webhook settings.
              </span>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Verify Token</label>
              <div style={styles.webhookUrlContainer}>
                <input
                  type="text"
                  value={whatsappVerifyToken}
                  onChange={(e) => setWhatsappVerifyToken(e.target.value)}
                  style={styles.input}
                  placeholder="your-secret-verify-token"
                />
                <button
                  type="button"
                  onClick={() => setWhatsappVerifyToken(crypto.randomUUID())}
                  style={styles.generateButton}
                  title="Generate random token"
                >
                  Generate
                </button>
              </div>
              <span style={styles.hint}>
                A secret token to verify webhook requests. Enter the same value in Meta&apos;s webhook settings.
              </span>
            </div>
          </div>

          <div style={styles.whatsappActions}>
            <button
              type="button"
              onClick={handleValidateWhatsAppCredentials}
              disabled={whatsappValidationStatus === 'validating' || !whatsappAccessToken.trim() || !whatsappPhoneNumberId.trim()}
              style={{
                ...styles.verifyButton,
                ...(whatsappValidationStatus === 'validating' || !whatsappAccessToken.trim() || !whatsappPhoneNumberId.trim() ? styles.buttonDisabled : {}),
              }}
            >
              {whatsappValidationStatus === 'validating' ? 'Verifying...' : 'Verify Credentials'}
            </button>
            {(whatsappAccessToken || whatsappPhoneNumberId) && (
              <button
                type="button"
                onClick={handleClearWhatsAppCredentials}
                style={styles.clearButton}
                title="Clear credentials"
              >
                Clear
              </button>
            )}
          </div>

          {/* Validation Status Display */}
          {whatsappValidationStatus === 'success' && (
            <div style={styles.whatsappSuccess}>
              <span style={styles.successIcon}>✓</span>
              <span>
                WhatsApp credentials verified successfully
              </span>
            </div>
          )}

          {whatsappValidationStatus === 'error' && whatsappError && (
            <div style={styles.whatsappError}>
              <span style={styles.errorIcon}>✗</span>
              <span>{whatsappError}</span>
            </div>
          )}

          {whatsappValidationStatus === 'idle' && (whatsappAccessToken || whatsappPhoneNumberId) && (
            <div style={styles.whatsappHint}>
              Click &quot;Verify Credentials&quot; to validate your WhatsApp configuration before saving.
            </div>
          )}
        </div>
      </div>

      {/* Stripe Payment Integration Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          <span style={styles.paymentIcon}>💳</span> Stripe Integration
        </h3>
        <p style={styles.sectionDescription}>
          Connect Stripe to enable payment processing and subscription management for this bot.
          Get your API keys from the{' '}
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={styles.helpLink}>
            Stripe Dashboard
          </a>.
        </p>

        <div style={styles.paymentSection}>
          {/* Test Mode Toggle */}
          <div style={styles.testModeToggle}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={stripeTestMode}
                onChange={(e) => handleStripeTestModeChange(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>Test Mode</span>
            </label>
            <span style={{
              ...styles.testModeBadge,
              ...(stripeTestMode ? styles.testModeBadgeTest : styles.testModeBadgeLive),
            }}>
              {stripeTestMode ? 'TEST' : 'LIVE'}
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Secret Key {stripeTestMode ? '(Test)' : '(Live)'}</label>
            <div style={styles.paymentInputRow}>
              <input
                type="password"
                value={stripeSecretKey}
                onChange={(e) => handleStripeSecretKeyChange(e.target.value)}
                style={{
                  ...styles.input,
                  ...styles.paymentInput,
                  ...(stripeValidationStatus === 'success' ? styles.inputSuccess : {}),
                  ...(stripeValidationStatus === 'error' ? styles.inputError : {}),
                }}
                placeholder={stripeTestMode ? 'sk_test_...' : 'sk_live_...'}
              />
              <button
                type="button"
                onClick={handleValidateStripeCredentials}
                disabled={stripeValidationStatus === 'validating' || !stripeSecretKey.trim()}
                style={{
                  ...styles.verifyButton,
                  ...(stripeValidationStatus === 'validating' || !stripeSecretKey.trim() ? styles.buttonDisabled : {}),
                }}
              >
                {stripeValidationStatus === 'validating' ? 'Verifying...' : 'Verify'}
              </button>
              {stripeSecretKey && (
                <button
                  type="button"
                  onClick={handleClearStripeCredentials}
                  style={styles.clearButton}
                  title="Clear credentials"
                >
                  ×
                </button>
              )}
            </div>
            <span style={styles.hint}>
              Your Stripe secret API key. Use test keys for development.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Webhook Secret (Optional)</label>
            <input
              type="password"
              value={stripeWebhookSecret}
              onChange={(e) => handleStripeWebhookSecretChange(e.target.value)}
              style={styles.input}
              placeholder="whsec_..."
            />
            <span style={styles.hint}>
              Webhook endpoint secret for signature verification.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Default Price ID (Optional)</label>
            <input
              type="text"
              value={stripeDefaultPriceId}
              onChange={(e) => handleStripeDefaultPriceIdChange(e.target.value)}
              style={styles.input}
              placeholder="price_..."
            />
            <span style={styles.hint}>
              Default price ID for subscriptions.
            </span>
          </div>

          {/* Validation Status Display */}
          {stripeValidationStatus === 'success' && (
            <div style={styles.paymentSuccess}>
              <span style={styles.successIcon}>✓</span>
              <span>
                Stripe connected successfully
                {stripeAccountId && <span style={styles.accountId}> (Account: {stripeAccountId})</span>}
              </span>
            </div>
          )}

          {stripeValidationStatus === 'error' && stripeError && (
            <div style={styles.paymentError}>
              <span style={styles.errorIcon}>✗</span>
              <span>{stripeError}</span>
            </div>
          )}

          {stripeValidationStatus === 'idle' && stripeSecretKey && (
            <div style={styles.paymentHint}>
              Click &quot;Verify&quot; to validate your Stripe API key before saving.
            </div>
          )}
        </div>
      </div>

      {renderTabActions('integrations', updateIntegrationsMutation, handleSaveIntegrations)}
    </>
  );

  /**
   * Render the Appearance & Tools tab content.
   */
  const renderAppearanceTab = () => (
    <>
      {/* Layout / App Appearance Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Layout / App Appearance</h3>
        <p style={styles.sectionDescription}>
          Configure which UI blocks appear in the user-facing app and in what order.
          Toggle visibility and drag to reorder blocks, or use the arrow buttons.
        </p>

        <div style={styles.blocksContainer}>
          {formState.uiBlocks.map((block, index) => {
            const blockInfo = getBlockInfo(block.type);
            const isDragging = draggedBlockIndex === index;
            const isDragOver = dragOverBlockIndex === index;
            return (
              <div
                key={block.id}
                draggable
                onDragStart={(e) => handleBlockDragStart(e, index)}
                onDragEnd={handleBlockDragEnd}
                onDragOver={(e) => handleBlockDragOver(e, index)}
                onDragLeave={handleBlockDragLeave}
                onDrop={(e) => handleBlockDrop(e, index)}
                style={{
                  ...styles.blockCard,
                  ...(block.visible ? {} : styles.blockCardHidden),
                  ...(isDragging ? styles.blockCardDragging : {}),
                  ...(isDragOver ? styles.blockCardDragOver : {}),
                }}
              >
                <div style={styles.blockCardHeader}>
                  <span style={styles.blockDragHandle} title="Drag to reorder">⋮⋮</span>
                  <span style={styles.blockNumber}>{index + 1}</span>
                  <label style={styles.blockCheckboxLabel}>
                    <input
                      type="checkbox"
                      checked={block.visible}
                      onChange={() => handleToggleBlockVisibility(index)}
                      style={styles.blockCheckbox}
                    />
                    <span style={styles.blockName}>{blockInfo.label}</span>
                  </label>
                  <span style={styles.blockTypeBadge}>{block.type}</span>
                  <div style={styles.blockActions}>
                    <button
                      type="button"
                      onClick={() => handleMoveBlockUp(index)}
                      disabled={index === 0}
                      style={{
                        ...styles.blockActionButton,
                        ...(index === 0 ? styles.blockActionButtonDisabled : {}),
                      }}
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveBlockDown(index)}
                      disabled={index === formState.uiBlocks.length - 1}
                      style={{
                        ...styles.blockActionButton,
                        ...(index === formState.uiBlocks.length - 1
                          ? styles.blockActionButtonDisabled
                          : {}),
                      }}
                      title="Move Down"
                    >
                      ↓
                    </button>
                  </div>
                </div>
                {blockInfo.description && (
                  <p style={styles.blockDescription}>{blockInfo.description}</p>
                )}
              </div>
            );
          })}
        </div>

        {formState.uiBlocks.length === 0 && (
          <div style={styles.blocksEmpty}>
            No UI blocks configured. Blocks will be initialized with defaults when you save.
          </div>
        )}
      </div>

      {/* App Branding Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>App Branding</h3>
        <p style={styles.sectionDescription}>
          Configure branding assets for the white-labeled web application.
        </p>

        {/* Logo Upload */}
        <div style={styles.field}>
          <label style={styles.label}>Logo</label>
          <div style={styles.uploadContainer}>
            {formState.logoUrl && (
              <div style={styles.uploadPreview}>
                <img
                  src={formState.logoUrl}
                  alt="Logo preview"
                  style={styles.uploadPreviewImage}
                />
                <button
                  type="button"
                  onClick={() => handleChange('logoUrl', '')}
                  style={styles.uploadRemoveButton}
                  title="Remove logo"
                >
                  &times;
                </button>
              </div>
            )}
            <div style={styles.uploadInputContainer}>
              <label style={styles.uploadLabel}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  onChange={handleLogoUpload}
                  style={styles.uploadInput}
                  disabled={isUploadingLogo}
                />
                <span style={styles.uploadButton}>
                  {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </span>
              </label>
              <span style={styles.uploadOr}>or</span>
              <input
                type="text"
                value={formState.logoUrl}
                onChange={(e) => handleChange('logoUrl', e.target.value)}
                style={{ ...styles.input, flex: 1 }}
                placeholder="Enter URL: https://example.com/logo.png"
              />
            </div>
            {logoUploadError && (
              <span style={styles.uploadError}>{logoUploadError}</span>
            )}
          </div>
          <span style={styles.hint}>Logo image displayed in the app header (PNG, JPG, SVG, WebP, or GIF)</span>
        </div>

        {/* Favicon Upload */}
        <div style={styles.field}>
          <label style={styles.label}>Favicon</label>
          <div style={styles.uploadContainer}>
            {formState.faviconUrl && (
              <div style={styles.uploadPreview}>
                <img
                  src={formState.faviconUrl}
                  alt="Favicon preview"
                  style={styles.uploadPreviewFavicon}
                />
                <button
                  type="button"
                  onClick={() => handleChange('faviconUrl', '')}
                  style={styles.uploadRemoveButton}
                  title="Remove favicon"
                >
                  &times;
                </button>
              </div>
            )}
            <div style={styles.uploadInputContainer}>
              <label style={styles.uploadLabel}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                  onChange={handleFaviconUpload}
                  style={styles.uploadInput}
                  disabled={isUploadingFavicon}
                />
                <span style={styles.uploadButton}>
                  {isUploadingFavicon ? 'Uploading...' : 'Upload Favicon'}
                </span>
              </label>
              <span style={styles.uploadOr}>or</span>
              <input
                type="text"
                value={formState.faviconUrl}
                onChange={(e) => handleChange('faviconUrl', e.target.value)}
                style={{ ...styles.input, flex: 1 }}
                placeholder="Enter URL: https://example.com/favicon.ico"
              />
            </div>
            {faviconUploadError && (
              <span style={styles.uploadError}>{faviconUploadError}</span>
            )}
          </div>
          <span style={styles.hint}>Favicon displayed in the browser tab (ICO, PNG, SVG, or other image formats)</span>
        </div>

        {/* Font Families */}
        <h4 style={styles.subsectionTitle}>Typography</h4>
        <div style={styles.fieldsGrid}>
          <div style={styles.field}>
            <label style={styles.label}>Header Font</label>
            <input
              type="text"
              value={formState.headerFont}
              onChange={(e) => handleChange('headerFont', e.target.value)}
              style={styles.input}
              placeholder="Inter, Roboto, Arial"
            />
            <span style={styles.hint}>Font family for headings (e.g., &quot;Inter&quot;, &quot;Roboto&quot;)</span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Body Font</label>
            <input
              type="text"
              value={formState.bodyFont}
              onChange={(e) => handleChange('bodyFont', e.target.value)}
              style={styles.input}
              placeholder="Open Sans, Lato, system-ui"
            />
            <span style={styles.hint}>Font family for body text (e.g., &quot;Open Sans&quot;, &quot;Lato&quot;)</span>
          </div>
        </div>

        {/* Color Palette */}
        <h4 style={styles.subsectionTitle}>Color Palette</h4>
        <p style={styles.sectionDescription}>
          Define the color scheme for the customer-facing app. Use hex color codes (e.g., #3B82F6).
        </p>
        <div style={styles.colorPaletteGrid}>
          <div style={styles.colorField}>
            <label style={styles.label}>Primary Color</label>
            <div style={styles.colorInputContainer}>
              <input
                type="color"
                value={formState.colorPalette.primary || '#3B82F6'}
                onChange={(e) => handleColorPaletteChange('primary', e.target.value)}
                style={styles.colorPicker}
              />
              <input
                type="text"
                value={formState.colorPalette.primary}
                onChange={(e) => handleColorPaletteChange('primary', e.target.value)}
                style={styles.colorTextInput}
                placeholder="#3B82F6"
              />
            </div>
            <span style={styles.hint}>Main brand color for buttons and links</span>
          </div>

          <div style={styles.colorField}>
            <label style={styles.label}>Secondary Color</label>
            <div style={styles.colorInputContainer}>
              <input
                type="color"
                value={formState.colorPalette.secondary || '#6366F1'}
                onChange={(e) => handleColorPaletteChange('secondary', e.target.value)}
                style={styles.colorPicker}
              />
              <input
                type="text"
                value={formState.colorPalette.secondary}
                onChange={(e) => handleColorPaletteChange('secondary', e.target.value)}
                style={styles.colorTextInput}
                placeholder="#6366F1"
              />
            </div>
            <span style={styles.hint}>Secondary accent color</span>
          </div>

          <div style={styles.colorField}>
            <label style={styles.label}>Background Color</label>
            <div style={styles.colorInputContainer}>
              <input
                type="color"
                value={formState.colorPalette.background || '#F9FAFB'}
                onChange={(e) => handleColorPaletteChange('background', e.target.value)}
                style={styles.colorPicker}
              />
              <input
                type="text"
                value={formState.colorPalette.background}
                onChange={(e) => handleColorPaletteChange('background', e.target.value)}
                style={styles.colorTextInput}
                placeholder="#F9FAFB"
              />
            </div>
            <span style={styles.hint}>Page background color</span>
          </div>

          <div style={styles.colorField}>
            <label style={styles.label}>Text Color</label>
            <div style={styles.colorInputContainer}>
              <input
                type="color"
                value={formState.colorPalette.text || '#111827'}
                onChange={(e) => handleColorPaletteChange('text', e.target.value)}
                style={styles.colorPicker}
              />
              <input
                type="text"
                value={formState.colorPalette.text}
                onChange={(e) => handleColorPaletteChange('text', e.target.value)}
                style={styles.colorTextInput}
                placeholder="#111827"
              />
            </div>
            <span style={styles.hint}>Main text color</span>
          </div>

          <div style={styles.colorField}>
            <label style={styles.label}>Accent Color</label>
            <div style={styles.colorInputContainer}>
              <input
                type="color"
                value={formState.colorPalette.accent || '#8B5CF6'}
                onChange={(e) => handleColorPaletteChange('accent', e.target.value)}
                style={styles.colorPicker}
              />
              <input
                type="text"
                value={formState.colorPalette.accent}
                onChange={(e) => handleColorPaletteChange('accent', e.target.value)}
                style={styles.colorTextInput}
                placeholder="#8B5CF6"
              />
            </div>
            <span style={styles.hint}>Highlight and accent color</span>
          </div>
        </div>

        {/* Onboarding Settings */}
        <h4 style={styles.subsectionTitle}>Web App Onboarding</h4>
        <div style={styles.field}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={formState.disableDesktopOnboarding}
              onChange={(e) => handleChange('disableDesktopOnboarding', e.target.checked)}
              style={styles.checkbox}
            />
            <span>Disable Desktop/Web Onboarding</span>
          </label>
          <span style={styles.hint}>
            When enabled, users will skip the web app onboarding flow. Useful when onboarding is done via WhatsApp or external form builders.
          </span>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>External Onboarding URL</label>
          <input
            type="url"
            value={formState.externalOnboardingUrl}
            onChange={(e) => handleChange('externalOnboardingUrl', e.target.value)}
            placeholder="https://example.com/onboarding"
            style={styles.input}
          />
          <span style={styles.hint}>
            When set, new users (without an existing account) will be redirected to this URL instead of the standard SMS verification flow.
          </span>
        </div>
      </div>

      {/* Tool Configuration Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Tool Configuration</h3>
        <p style={styles.sectionDescription}>
          Enable and configure tools for this bot. Each tool can be customized with specific settings.
        </p>

        {toolsQuery.isLoading ? (
          <div style={styles.toolsLoading}>Loading available tools...</div>
        ) : toolsQuery.error ? (
          <div style={styles.toolsError}>
            Error loading tools: {toolsQuery.error.message}
          </div>
        ) : toolsQuery.data?.tools.length === 0 ? (
          <div style={styles.toolsEmpty}>No tools available.</div>
        ) : (
          <div style={styles.toolsGrid}>
            {toolsQuery.data?.tools.map((tool: { id: string; name: string; description: string | null; configSchema?: unknown }) => {
              const toolId = tool.id;
              const isEnabled = formState.enabledTools.includes(toolId);
              const toolConfig = formState.toolConfigs[toolId] || {};
              const configSchema = tool.configSchema as ToolConfigSchema | null;

              return (
                <div key={toolId} style={styles.toolCard}>
                  <div style={styles.toolHeader}>
                    <label style={styles.toolCheckboxLabel}>
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => handleToolToggle(toolId, e.target.checked)}
                        style={styles.toolCheckbox}
                      />
                      <span style={styles.toolName}>{tool.name}</span>
                    </label>
                    <span style={styles.toolBadge}>{toolId}</span>
                  </div>
                  <p style={styles.toolDescription}>{tool.description}</p>

                  {isEnabled && configSchema?.properties && (
                    <div style={styles.toolConfigFields}>
                      {Object.entries(configSchema.properties).map(([propKey, propSchema]) => (
                        <div key={propKey} style={styles.toolConfigField}>
                          {propSchema.type === 'boolean' ? (
                            <label style={styles.toolConfigCheckboxLabel}>
                              <input
                                type="checkbox"
                                checked={
                                  toolConfig[propKey] !== undefined
                                    ? Boolean(toolConfig[propKey])
                                    : Boolean(propSchema.default)
                                }
                                onChange={(e) =>
                                  handleToolConfigChange(toolId, propKey, e.target.checked)
                                }
                                style={styles.toolConfigCheckbox}
                              />
                              <span>{propSchema.description}</span>
                            </label>
                          ) : propSchema.type === 'number' ? (
                            <>
                              <label style={styles.toolConfigLabel}>
                                {propSchema.description}
                              </label>
                              <input
                                type="number"
                                value={
                                  toolConfig[propKey] !== undefined
                                    ? Number(toolConfig[propKey])
                                    : Number(propSchema.default)
                                }
                                onChange={(e) =>
                                  handleToolConfigChange(
                                    toolId,
                                    propKey,
                                    e.target.value ? parseInt(e.target.value, 10) : propSchema.default
                                  )
                                }
                                style={styles.toolConfigInput}
                                min={0}
                              />
                            </>
                          ) : propSchema.options ? (
                            <>
                              <label style={styles.toolConfigLabel}>
                                {propSchema.description}
                              </label>
                              <select
                                value={
                                  toolConfig[propKey] !== undefined
                                    ? String(toolConfig[propKey])
                                    : String(propSchema.default)
                                }
                                onChange={(e) =>
                                  handleToolConfigChange(toolId, propKey, e.target.value)
                                }
                                style={styles.toolConfigSelect}
                              >
                                {propSchema.options.map((option) => (
                                  <option key={option} value={option}>
                                    {option.replace(/_/g, ' ')}
                                  </option>
                                ))}
                              </select>
                            </>
                          ) : (
                            <>
                              <label style={styles.toolConfigLabel}>
                                {propSchema.description}
                              </label>
                              <input
                                type="text"
                                value={
                                  toolConfig[propKey] !== undefined
                                    ? String(toolConfig[propKey])
                                    : String(propSchema.default)
                                }
                                onChange={(e) =>
                                  handleToolConfigChange(toolId, propKey, e.target.value)
                                }
                                style={styles.toolConfigInput}
                              />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Scheduling Mode Configuration */}
                  {isEnabled && (
                    <div style={styles.schedulingModeSection}>
                      <div style={styles.schedulingModeHeader}>
                        <span style={styles.schedulingModeTitle}>Scheduling Mode</span>
                      </div>
                      <div style={styles.schedulingModeContent}>
                        <div style={styles.schedulingModeField}>
                          <label style={styles.toolConfigLabel}>
                            When should this tool run?
                          </label>
                          <select
                            value={
                              toolConfig.__scheduleMode !== undefined
                                ? String(toolConfig.__scheduleMode)
                                : 'on_demand'
                            }
                            onChange={(e) =>
                              handleToolConfigChange(toolId, '__scheduleMode', e.target.value)
                            }
                            style={styles.toolConfigSelect}
                          >
                            <option value="on_demand">On Demand (during conversation)</option>
                            <option value="cron">Cron (scheduled background execution)</option>
                          </select>
                        </div>
                        {toolConfig.__scheduleMode === 'cron' && (
                          <div style={styles.schedulingModeField}>
                            <label style={styles.toolConfigLabel}>
                              Cron Expression
                            </label>
                            <input
                              type="text"
                              value={
                                toolConfig.__cronExpression !== undefined
                                  ? String(toolConfig.__cronExpression)
                                  : ''
                              }
                              onChange={(e) =>
                                handleToolConfigChange(toolId, '__cronExpression', e.target.value)
                              }
                              style={styles.toolConfigInput}
                              placeholder="e.g., 0 */6 * * * (every 6 hours)"
                            />
                            <span style={styles.cronHint}>
                              Standard cron format: minute hour day month weekday
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {renderTabActions('appearance', updateAppearanceMutation, handleSaveAppearance)}
    </>
  );

  /**
   * Render the Analytics & Summaries tab content.
   */
  const renderAnalyticsTab = () => {
    // Get current configs from toolConfigs or initialize with defaults
    const analyticsConfig = (formState.toolConfigs.analytics || {}) as Record<string, unknown>;
    const summariesConfig = (formState.toolConfigs.summaries || {}) as Record<string, unknown>;

    const isAnalyticsEnabled = Boolean(analyticsConfig.enabled);
    const trackGoalCompletion = Boolean(analyticsConfig.trackGoalCompletion);
    const analyticsInstructions = (analyticsConfig.instructions as string) || '';

    const dailyPromptTemplate = (summariesConfig.dailyPromptTemplate as string) || '';
    const weeklyPromptTemplate = (summariesConfig.weeklyPromptTemplate as string) || '';

    return (
      <>
        {/* Message Analytics Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Message Analytics</h3>
          <p style={styles.sectionDescription}>
            Enable AI-powered analytics extraction from user messages. When enabled, the AI will analyze
            each message for sentiment, topics, intent, and other insights.
          </p>

          <div style={styles.field}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isAnalyticsEnabled}
                onChange={(e) => handleToolConfigChange('analytics', 'enabled', e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>Enable Message Analytics</span>
            </label>
            <span style={styles.hint}>
              When enabled, the AI will extract analytics data (classification, topic, intent) from each user message.
            </span>
          </div>

          {isAnalyticsEnabled && (
            <div style={styles.analyticsConfigSection}>
              <div style={styles.field}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={trackGoalCompletion}
                    onChange={(e) => handleToolConfigChange('analytics', 'trackGoalCompletion', e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>Track Goal Completion</span>
                </label>
                <span style={styles.hint}>
                  When enabled, the AI will also detect when users mention completing goals and flag them for tracking.
                </span>
              </div>

              <div style={styles.field}>
                <div style={styles.labelWithButton}>
                  <label style={styles.label}>Custom Analytics Prompt</label>
                  <button
                    type="button"
                    onClick={() => {
                      const defaultPrompt = `Extract the following analytics from the user's message:
- classification: (OPTIONAL) Only set if clearly applicable: "win" (breakthrough/achievement), "struggle" (difficulty/challenge), "schema" (core belief/self-talk pattern), "insight" (realization/understanding). Leave out if message doesn't clearly fit.
- topic: The main topic or subject being discussed
- intent: What the user is trying to accomplish (question, request, statement, greeting, etc.)`;
                      setAnalyticsTestDialog({
                        isOpen: true,
                        prompt: analyticsInstructions || defaultPrompt,
                        userMessage: '',
                        trackGoalCompletion,
                        isTesting: false,
                        result: null,
                        error: null,
                      });
                    }}
                    style={styles.testPromptButton}
                  >
                    Test Prompt
                  </button>
                </div>
                <textarea
                  value={analyticsInstructions}
                  onChange={(e) => handleToolConfigChange('analytics', 'instructions', e.target.value)}
                  style={styles.textarea}
                  rows={6}
                  placeholder={`Extract the following analytics from the user's message:
- classification: (OPTIONAL) Only set if clearly applicable: "win" (breakthrough/achievement), "struggle" (difficulty/challenge), "schema" (core belief/self-talk pattern), "insight" (realization/understanding). Leave out if message doesn't clearly fit.
- topic: The main topic or subject being discussed
- intent: What the user is trying to accomplish (question, request, statement, greeting, etc.)`}
                />
                <span style={styles.hint}>
                  Customize what analytics the AI should extract from messages. Leave empty to use the default prompt.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Conversation Summaries Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Conversation Summaries</h3>
          <p style={styles.sectionDescription}>
            Customize the AI prompts used to generate daily and weekly conversation summaries.
            These summaries help maintain context across conversations.
          </p>

          <div style={styles.field}>
            <div style={styles.labelWithButton}>
              <label style={styles.label}>Daily Summary Prompt</label>
              <button
                type="button"
                onClick={() => {
                  const defaultPrompt = `You are a supportive wellness coach creating a summary of today's conversations with {{user.name || 'the user'}}.

## Conversation History
{{conversationHistory}}

Based on the conversation history above, create a concise summary that captures:
1. Key topics discussed
2. User's emotional state and concerns
3. Any goals, commitments, or action items mentioned
4. Important information shared by the user

Keep the summary factual and concise (2-3 short paragraphs maximum). Only include information that is actually present in the conversation history - do not make up or assume any details.`;
                  setSummaryTestDialog({
                    isOpen: true,
                    type: 'daily',
                    prompt: dailyPromptTemplate || defaultPrompt,
                    mockMessages: [{ role: 'user', content: '' }],
                    previousSummary: '',
                    isTesting: false,
                    result: null,
                    error: null,
                    userSearchQuery: '',
                    selectedUserId: null,
                    selectedUserName: null,
                    isLoadingUserMessages: false,
                    loadEntireConversation: false,
                  });
                }}
                style={styles.testPromptButton}
              >
                Test Prompt
              </button>
            </div>
            <textarea
              value={dailyPromptTemplate}
              onChange={(e) => handleToolConfigChange('summaries', 'dailyPromptTemplate', e.target.value)}
              style={styles.textarea}
              rows={8}
              placeholder={`You are a supportive wellness coach creating a summary of today's conversations with {{user.name || 'the user'}}.

## Conversation History
{{conversationHistory}}

Based on the conversation history above, create a concise summary that captures:
1. Key topics discussed
2. User's emotional state and concerns
3. Any goals, commitments, or action items mentioned
4. Important information shared by the user

Keep the summary factual and concise (2-3 short paragraphs maximum). Only include information that is actually present in the conversation history - do not make up or assume any details.`}
            />
            <div style={styles.hintWithButton}>
              <span style={styles.hint}>
                Template for generating daily summaries. Supports variables: {'{{user.name}}'}, {'{{userContext}}'}, {'{{conversationHistory}}'}
              </span>
              {!dailyPromptTemplate && (
                <button
                  type="button"
                  onClick={() => {
                    const defaultPrompt = `You are a supportive wellness coach creating a summary of today's conversations with {{user.name || 'the user'}}.

## Conversation History
{{conversationHistory}}

Based on the conversation history above, create a concise summary that captures:
1. Key topics discussed
2. User's emotional state and concerns
3. Any goals, commitments, or action items mentioned
4. Important information shared by the user

Keep the summary factual and concise (2-3 short paragraphs maximum). Only include information that is actually present in the conversation history - do not make up or assume any details.`;
                    handleToolConfigChange('summaries', 'dailyPromptTemplate', defaultPrompt);
                  }}
                  style={styles.useDefaultButton}
                >
                  Use Default
                </button>
              )}
            </div>
          </div>

          <div style={styles.field}>
            <div style={styles.labelWithButton}>
              <label style={styles.label}>Weekly Summary Prompt</label>
              <button
                type="button"
                onClick={() => {
                  const defaultPrompt = `You are a supportive wellness coach creating a summary of this week's conversations with {{user.name || 'the user'}}.

## Conversation History
{{conversationHistory}}

Based on the conversation history above, create a comprehensive summary that captures:
1. Recurring themes and patterns
2. Progress made on goals
3. Challenges faced and how they were addressed
4. Key insights about the user's journey

Keep the summary informative but concise (3-4 paragraphs maximum). Only include information that is actually present in the conversation history - do not make up or assume any details.`;
                  setSummaryTestDialog({
                    isOpen: true,
                    type: 'weekly',
                    prompt: weeklyPromptTemplate || defaultPrompt,
                    mockMessages: [{ role: 'user', content: '' }],
                    previousSummary: '',
                    isTesting: false,
                    result: null,
                    error: null,
                    userSearchQuery: '',
                    selectedUserId: null,
                    selectedUserName: null,
                    isLoadingUserMessages: false,
                    loadEntireConversation: false,
                  });
                }}
                style={styles.testPromptButton}
              >
                Test Prompt
              </button>
            </div>
            <textarea
              value={weeklyPromptTemplate}
              onChange={(e) => handleToolConfigChange('summaries', 'weeklyPromptTemplate', e.target.value)}
              style={styles.textarea}
              rows={8}
              placeholder={`You are a supportive wellness coach creating a summary of this week's conversations with {{user.name || 'the user'}}.

## Conversation History
{{conversationHistory}}

Based on the conversation history above, create a comprehensive summary that captures:
1. Recurring themes and patterns
2. Progress made on goals
3. Challenges faced and how they were addressed
4. Key insights about the user's journey

Keep the summary informative but concise (3-4 paragraphs maximum). Only include information that is actually present in the conversation history - do not make up or assume any details.`}
            />
            <div style={styles.hintWithButton}>
              <span style={styles.hint}>
                Template for generating weekly summaries. Supports variables: {'{{user.name}}'}, {'{{userContext}}'}, {'{{conversationHistory}}'}
              </span>
              {!weeklyPromptTemplate && (
                <button
                  type="button"
                  onClick={() => {
                    const defaultPrompt = `You are a supportive wellness coach creating a summary of this week's conversations with {{user.name || 'the user'}}.

## Conversation History
{{conversationHistory}}

Based on the conversation history above, create a comprehensive summary that captures:
1. Recurring themes and patterns
2. Progress made on goals
3. Challenges faced and how they were addressed
4. Key insights about the user's journey

Keep the summary informative but concise (3-4 paragraphs maximum). Only include information that is actually present in the conversation history - do not make up or assume any details.`;
                    handleToolConfigChange('summaries', 'weeklyPromptTemplate', defaultPrompt);
                  }}
                  style={styles.useDefaultButton}
                >
                  Use Default
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Summary Model Override */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Summary AI Model</h3>
          <p style={styles.sectionDescription}>
            Override the default AI model used for generating daily and weekly summaries for this bot.
          </p>

          <div style={styles.field}>
            <label style={styles.label}>AI Model</label>
            <input
              type="text"
              value={formState.summaryModel}
              onChange={(e) => handleChange('summaryModel', e.target.value)}
              placeholder="e.g., openai/gpt-4o-mini (leave empty to use system default)"
              style={styles.input}
            />
            <span style={styles.hint}>
              Specify the AI model to use for generating summaries. If left empty, the system default will be used.
            </span>
          </div>
        </div>

        {renderTabActions('analytics', updateAnalyticsMutation, handleSaveAnalytics)}
      </>
    );
  };

  /**
   * Handles saving a plan (create or update based on editingPlan).
   */
  const handleSavePlan = async (data: PlanFormData & { id?: string }): Promise<void> => {
    if (!botId) return;

    setPlanSaveError(undefined);

    if (editingPlan && data.id) {
      // Update existing plan
      await updatePlanMutation.mutateAsync({
        id: data.id,
        name: data.name,
        planKey: data.planKey,
        description: data.description ?? null,
        priceAmount: data.priceAmount,
        currency: data.currency,
        billingInterval: data.billingInterval,
        intervalCount: data.intervalCount,
        status: data.status,
        features: data.features,
        limits: data.limits,
        platformIds: data.platformIds,
        trialDays: data.trialDays,
        isDefault: data.isDefault,
        isFeatured: data.isFeatured,
      });
    } else {
      // Create new plan
      await createPlanMutation.mutateAsync({
        botId,
        name: data.name,
        planKey: data.planKey,
        description: data.description,
        priceAmount: data.priceAmount,
        currency: data.currency,
        billingInterval: data.billingInterval,
        intervalCount: data.intervalCount,
        trialDays: data.trialDays,
        status: data.status,
        features: data.features,
        limits: data.limits,
        platformIds: data.platformIds,
        isDefault: data.isDefault,
        isFeatured: data.isFeatured,
      });
    }
  };

  /**
   * Handles the deletion of a plan with confirmation.
   */
  const handleDeletePlan = (plan: { id: string; name: string }) => {
    if (window.confirm(`Are you sure you want to delete the plan "${plan.name}"? This action cannot be undone.`)) {
      deletePlanMutation.mutate({ id: plan.id });
    }
  };

  /**
   * Handles moving a plan up in the sort order (swap with the previous plan).
   */
  const handleMovePlanUp = async (plans: Array<{ id: string; sortOrder: number }>, currentIndex: number) => {
    if (currentIndex <= 0) return;

    const currentPlan = plans[currentIndex];
    const previousPlan = plans[currentIndex - 1];

    // Swap the sortOrder values
    await Promise.all([
      updatePlanMutation.mutateAsync({
        id: currentPlan.id,
        sortOrder: previousPlan.sortOrder,
      }),
      updatePlanMutation.mutateAsync({
        id: previousPlan.id,
        sortOrder: currentPlan.sortOrder,
      }),
    ]);

    // Invalidate the plans list to refresh the UI
    utils.plans.list.invalidate({ botId: botId ?? '' });
  };

  /**
   * Handles moving a plan down in the sort order (swap with the next plan).
   */
  const handleMovePlanDown = async (plans: Array<{ id: string; sortOrder: number }>, currentIndex: number) => {
    if (currentIndex >= plans.length - 1) return;

    const currentPlan = plans[currentIndex];
    const nextPlan = plans[currentIndex + 1];

    // Swap the sortOrder values
    await Promise.all([
      updatePlanMutation.mutateAsync({
        id: currentPlan.id,
        sortOrder: nextPlan.sortOrder,
      }),
      updatePlanMutation.mutateAsync({
        id: nextPlan.id,
        sortOrder: currentPlan.sortOrder,
      }),
    ]);

    // Invalidate the plans list to refresh the UI
    utils.plans.list.invalidate({ botId: botId ?? '' });
  };

  /**
   * Renders the Plans tab content with a list of subscription plans.
   */
  const renderPlansTab = () => {
    const plans = plansQuery.data?.plans ?? [];
    const isLoading = plansQuery.isLoading;
    const error = plansQuery.error;

    return (
      <>
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>Subscription Plans</h3>
              <p style={styles.sectionDescription}>
                Configure the subscription plans available for this bot. Plans define pricing, features, and limits for your users.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingPlan(null);
                setPlanSaveError(undefined);
                setIsPlanModalOpen(true);
              }}
              style={styles.createPlanButton}
            >
              + Create Plan
            </button>
          </div>

          {isLoading && (
            <div style={styles.loading}>Loading plans...</div>
          )}

          {error && (
            <div style={styles.error}>
              Error loading plans: {error.message}
            </div>
          )}

          {!isLoading && !error && plans.length === 0 && (
            <div style={styles.emptyState}>
              <p style={styles.emptyStateText}>No subscription plans configured for this bot yet.</p>
              <p style={styles.emptyStateHint}>
                Click "Create Plan" above to add your first subscription plan.
              </p>
            </div>
          )}

          {!isLoading && !error && plans.length > 0 && (
            <div style={styles.plansList}>
              {plans.map((plan, planIndex) => (
                <div key={plan.id} style={styles.planCard}>
                  <div style={styles.planHeader}>
                    <div style={styles.planNameRow}>
                      <span style={styles.planName}>{plan.name}</span>
                      {plan.isDefault && (
                        <span style={styles.planBadge}>Default</span>
                      )}
                      {plan.isFeatured && (
                        <span style={{ ...styles.planBadge, backgroundColor: '#8b5cf6' }}>Featured</span>
                      )}
                      <span style={{
                        ...styles.planBadge,
                        backgroundColor: plan.status === 'active' ? '#22c55e' : plan.status === 'archived' ? '#64748b' : '#f59e0b',
                      }}>
                        {plan.status}
                      </span>
                    </div>
                    <span style={styles.planKey}>{plan.planKey}</span>
                  </div>
                  {plan.description && (
                    <p style={styles.planDescription}>{plan.description}</p>
                  )}
                  <div style={styles.planDetails}>
                    <span style={styles.planPrice}>
                      {plan.displayPrice || `${plan.currency.toUpperCase()} ${(plan.priceAmount / 100).toFixed(2)}`}
                      <span style={styles.planInterval}>
                        {(plan.intervalCount ?? 1) > 1
                          ? ` / ${plan.intervalCount} ${plan.billingInterval}`
                          : `/${plan.billingInterval}`}
                      </span>
                    </span>
                    {plan.trialDays != null && plan.trialDays > 0 && (
                      <span style={styles.planTrial}>{plan.trialDays} day trial</span>
                    )}
                  </div>
                  {plan.features && (() => {
                    const featureList = Array.isArray(plan.features)
                      ? plan.features as string[]
                      : (plan.features as { featureList?: string[] })?.featureList ?? [];
                    return featureList.length > 0 ? (
                      <ul style={styles.planFeatures}>
                        {featureList.slice(0, 3).map((feature, idx) => (
                          <li key={idx} style={styles.planFeatureItem}>{feature}</li>
                        ))}
                        {featureList.length > 3 && (
                          <li style={styles.planFeatureMore}>+{featureList.length - 3} more features</li>
                        )}
                      </ul>
                    ) : null;
                  })()}
                  <div style={styles.planActions}>
                    <button
                      type="button"
                      onClick={() => {
                        // Normalize features to the expected format
                        const normalizedFeatures = plan.features
                          ? (Array.isArray(plan.features)
                            ? { featureList: plan.features as string[], highlights: [], excludedFeatures: [] }
                            : {
                                featureList: (plan.features as { featureList?: string[] })?.featureList ?? [],
                                highlights: (plan.features as { highlights?: string[] })?.highlights ?? [],
                                excludedFeatures: (plan.features as { excludedFeatures?: string[] })?.excludedFeatures ?? [],
                              })
                          : null;
                        setEditingPlan({
                          id: plan.id,
                          planKey: plan.planKey,
                          name: plan.name,
                          description: plan.description,
                          priceAmount: plan.priceAmount,
                          currency: plan.currency,
                          billingInterval: plan.billingInterval as PlanData['billingInterval'],
                          intervalCount: plan.intervalCount ?? 1,
                          status: plan.status as PlanData['status'],
                          features: normalizedFeatures,
                          limits: plan.limits as PlanData['limits'],
                          platformIds: plan.platformIds as PlanData['platformIds'],
                          trialDays: plan.trialDays ?? 0,
                          isDefault: plan.isDefault ?? false,
                          isFeatured: plan.isFeatured ?? false,
                        });
                        setPlanSaveError(undefined);
                        setIsPlanModalOpen(true);
                      }}
                      style={styles.planEditButton}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePlan({ id: plan.id, name: plan.name })}
                      disabled={deletePlanMutation.isPending}
                      style={{
                        ...styles.planDeleteButton,
                        ...(deletePlanMutation.isPending ? styles.buttonDisabled : {}),
                      }}
                    >
                      {deletePlanMutation.isPending ? 'Deleting...' : 'Delete'}
                    </button>
                    <div style={styles.planReorderButtons}>
                      <button
                        type="button"
                        onClick={() => handleMovePlanUp(plans, planIndex)}
                        disabled={planIndex === 0 || updatePlanMutation.isPending}
                        style={{
                          ...styles.planMoveButton,
                          ...(planIndex === 0 || updatePlanMutation.isPending ? styles.planMoveButtonDisabled : {}),
                        }}
                        title="Move Up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMovePlanDown(plans, planIndex)}
                        disabled={planIndex === plans.length - 1 || updatePlanMutation.isPending}
                        style={{
                          ...styles.planMoveButton,
                          ...(planIndex === plans.length - 1 || updatePlanMutation.isPending ? styles.planMoveButtonDisabled : {}),
                        }}
                        title="Move Down"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <PlanModal
          isOpen={isPlanModalOpen}
          onClose={() => {
            setIsPlanModalOpen(false);
            setEditingPlan(null);
            setPlanSaveError(undefined);
          }}
          onSubmit={handleSavePlan}
          editingPlan={editingPlan}
          isSubmitting={createPlanMutation.isPending || updatePlanMutation.isPending}
          error={planSaveError}
        />
      </>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/bot/details')} style={styles.backButton}>
          &larr; Back to Bot Details
        </button>
        <h1 style={styles.title}>Edit Bot</h1>
      </div>

      <div style={styles.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab.id ? styles.tabButtonActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.card}>
        {activeTab === 'general' && renderGeneralTab()}
        {activeTab === 'quick-response' && renderQuickResponseTab()}
        {activeTab === 'voice' && renderVoiceTab()}
        {activeTab === 'integrations' && renderIntegrationsTab()}
        {activeTab === 'appearance' && renderAppearanceTab()}
        {activeTab === 'analytics' && renderAnalyticsTab()}
        {activeTab === 'plans' && renderPlansTab()}
      </div>

      {/* Summary Prompt Test Dialog */}
      {summaryTestDialog.isOpen && (
        <div style={styles.dialogOverlay} onClick={() => setSummaryTestDialog((prev) => ({ ...prev, isOpen: false }))}>
          <div style={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogHeader}>
              <h3 style={styles.dialogTitle}>
                Test {summaryTestDialog.type === 'daily' ? 'Daily' : 'Weekly'} Summary Prompt
              </h3>
              <button
                onClick={() => setSummaryTestDialog((prev) => ({ ...prev, isOpen: false }))}
                style={styles.dialogCloseButton}
              >
                ×
              </button>
            </div>
            <div style={styles.dialogBody}>
              <p style={styles.dialogDescription}>
                Provide mock conversation messages or load real conversations from existing users to test how the summary prompt generates summaries.
              </p>

              {/* User Search Section */}
              <div style={styles.userSearchSection}>
                <label style={styles.dialogLabel}>Load Conversation from User (optional)</label>
                <div style={styles.userSearchInputRow}>
                  <input
                    type="text"
                    value={summaryTestDialog.userSearchQuery}
                    onChange={(e) => {
                      setSummaryTestDialog((prev) => ({
                        ...prev,
                        userSearchQuery: e.target.value,
                        selectedUserId: null,
                        selectedUserName: null,
                      }));
                    }}
                    placeholder="Search by name or phone number..."
                    style={styles.userSearchInput}
                    disabled={summaryTestDialog.isTesting || summaryTestDialog.isLoadingUserMessages}
                  />
                  {summaryTestDialog.selectedUserName && (
                    <button
                      type="button"
                      onClick={() => {
                        setSummaryTestDialog((prev) => ({
                          ...prev,
                          userSearchQuery: '',
                          selectedUserId: null,
                          selectedUserName: null,
                          mockMessages: [{ role: 'user', content: '' }],
                          loadEntireConversation: false,
                        }));
                      }}
                      style={styles.clearUserButton}
                      disabled={summaryTestDialog.isTesting}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {summaryTestDialog.selectedUserName && (
                  <div style={styles.selectedUserBadge}>
                    <span style={styles.selectedUserIcon}>👤</span>
                    <span>
                      Loaded conversation from: <strong>{summaryTestDialog.selectedUserName}</strong>
                      {!summaryTestDialog.loadEntireConversation && (
                        <span style={{ color: '#64748b', marginLeft: '8px' }}>
                          ({summaryTestDialog.type === 'daily' ? 'last 24 hours' : 'last 7 days'})
                        </span>
                      )}
                      {summaryTestDialog.loadEntireConversation && (
                        <span style={{ color: '#64748b', marginLeft: '8px' }}>(entire history)</span>
                      )}
                    </span>
                  </div>
                )}
                {summaryTestDialog.selectedUserName && !summaryTestDialog.loadEntireConversation && (
                  <button
                    type="button"
                    onClick={() => {
                      setSummaryTestDialog((prev) => ({
                        ...prev,
                        loadEntireConversation: true,
                        isLoadingUserMessages: true,
                      }));
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      color: '#3b82f6',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                    disabled={summaryTestDialog.isTesting || summaryTestDialog.isLoadingUserMessages}
                  >
                    Load Entire Conversation
                  </button>
                )}
                {summaryTestDialog.selectedUserName && summaryTestDialog.loadEntireConversation && (
                  <button
                    type="button"
                    onClick={() => {
                      setSummaryTestDialog((prev) => ({
                        ...prev,
                        loadEntireConversation: false,
                        isLoadingUserMessages: true,
                      }));
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      color: '#64748b',
                      backgroundColor: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                    disabled={summaryTestDialog.isTesting || summaryTestDialog.isLoadingUserMessages}
                  >
                    Filter to {summaryTestDialog.type === 'daily' ? 'Last 24 Hours' : 'Last 7 Days'}
                  </button>
                )}
                {summaryTestDialog.userSearchQuery.length >= 2 && !summaryTestDialog.selectedUserId && (
                  <div style={styles.userSearchResults}>
                    {userSearchQuery.isLoading ? (
                      <div style={styles.userSearchLoading}>Searching users...</div>
                    ) : userSearchQuery.data?.users && userSearchQuery.data.users.length > 0 ? (
                      userSearchQuery.data.users.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => {
                            setSummaryTestDialog((prev) => ({
                              ...prev,
                              selectedUserId: user.id,
                              selectedUserName: user.name || user.phoneNumber || 'Unknown User',
                              userSearchQuery: '',
                              isLoadingUserMessages: true,
                            }));
                          }}
                          style={styles.userSearchResultItem}
                        >
                          <span style={styles.userSearchResultName}>{user.name || 'Unknown'}</span>
                          {user.phoneNumber && (
                            <span style={styles.userSearchResultPhone}>{user.phoneNumber}</span>
                          )}
                        </button>
                      ))
                    ) : (
                      <div style={styles.userSearchEmpty}>No users found</div>
                    )}
                  </div>
                )}
                {summaryTestDialog.isLoadingUserMessages && (
                  <div style={styles.loadingUserMessages}>Loading conversation messages...</div>
                )}
              </div>

              {/* Mock Messages */}
              <div style={styles.mockMessagesSection}>
                <label style={styles.dialogLabel}>Mock Conversation Messages</label>
                {summaryTestDialog.mockMessages.map((msg, index) => (
                  <div key={index} style={styles.mockMessageRow}>
                    <select
                      value={msg.role}
                      onChange={(e) => {
                        const newMessages = [...summaryTestDialog.mockMessages];
                        newMessages[index] = { ...msg, role: e.target.value as 'user' | 'assistant' };
                        setSummaryTestDialog((prev) => ({ ...prev, mockMessages: newMessages }));
                      }}
                      style={styles.mockMessageRoleSelect}
                      disabled={summaryTestDialog.isTesting}
                    >
                      <option value="user">User</option>
                      <option value="assistant">Assistant</option>
                    </select>
                    <input
                      type="text"
                      value={msg.content}
                      onChange={(e) => {
                        const newMessages = [...summaryTestDialog.mockMessages];
                        newMessages[index] = { ...msg, content: e.target.value };
                        setSummaryTestDialog((prev) => ({ ...prev, mockMessages: newMessages }));
                      }}
                      placeholder="Enter message content..."
                      style={styles.mockMessageInput}
                      disabled={summaryTestDialog.isTesting}
                    />
                    {summaryTestDialog.mockMessages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newMessages = summaryTestDialog.mockMessages.filter((_, i) => i !== index);
                          setSummaryTestDialog((prev) => ({ ...prev, mockMessages: newMessages }));
                        }}
                        style={styles.removeMessageButton}
                        disabled={summaryTestDialog.isTesting}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setSummaryTestDialog((prev) => ({
                      ...prev,
                      mockMessages: [...prev.mockMessages, { role: 'user', content: '' }],
                    }));
                  }}
                  style={styles.addMessageButton}
                  disabled={summaryTestDialog.isTesting}
                >
                  + Add Message
                </button>
              </div>

              {/* Previous Summary (optional) */}
              <div style={styles.dialogField}>
                <label style={styles.dialogLabel}>Previous Summary (optional)</label>
                <textarea
                  value={summaryTestDialog.previousSummary}
                  onChange={(e) => setSummaryTestDialog((prev) => ({ ...prev, previousSummary: e.target.value }))}
                  placeholder="Enter a previous summary for context (optional)..."
                  style={styles.dialogTextarea}
                  rows={3}
                  disabled={summaryTestDialog.isTesting}
                />
              </div>

              {/* Result Display */}
              {summaryTestDialog.result && (
                <div style={styles.testResultSuccess}>
                  <div style={styles.testResultHeader}>
                    <span style={styles.testResultIcon}>✓</span>
                    <span>Summary Generated</span>
                  </div>
                  <div style={styles.testResultContent}>
                    {summaryTestDialog.result.summary}
                  </div>
                  <div style={styles.testResultMetrics}>
                    <div style={styles.testResultMetric}>
                      <span style={styles.testResultMetricLabel}>Model</span>
                      <span style={styles.testResultMetricValue}>{summaryTestDialog.result.model}</span>
                    </div>
                    <div style={styles.testResultMetric}>
                      <span style={styles.testResultMetricLabel}>Latency</span>
                      <span style={styles.testResultMetricValue}>{summaryTestDialog.result.latencyMs}ms</span>
                    </div>
                    <div style={styles.testResultMetric}>
                      <span style={styles.testResultMetricLabel}>Tokens</span>
                      <span style={styles.testResultMetricValue}>{summaryTestDialog.result.usage.totalTokens}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {summaryTestDialog.error && (
                <div style={styles.testResultError}>
                  <span style={styles.testResultIcon}>✕</span>
                  <span>{summaryTestDialog.error}</span>
                </div>
              )}
            </div>
            <div style={styles.dialogFooter}>
              <button
                onClick={() => setSummaryTestDialog((prev) => ({ ...prev, isOpen: false }))}
                style={styles.dialogCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Validate that we have at least one message with content
                  const validMessages = summaryTestDialog.mockMessages.filter((m) => m.content.trim());
                  if (validMessages.length === 0) {
                    setSummaryTestDialog((prev) => ({
                      ...prev,
                      error: 'Please enter at least one message with content.',
                    }));
                    return;
                  }

                  setSummaryTestDialog((prev) => ({ ...prev, isTesting: true, result: null, error: null }));
                  testSummaryPromptMutation.mutate({
                    prompt: summaryTestDialog.prompt,
                    recentMessages: validMessages,
                    previousSummary: summaryTestDialog.previousSummary || undefined,
                    summaryType: summaryTestDialog.type,
                  });
                }}
                disabled={summaryTestDialog.isTesting || summaryTestDialog.mockMessages.every((m) => !m.content.trim())}
                style={{
                  ...styles.dialogTestButton,
                  ...(summaryTestDialog.isTesting || summaryTestDialog.mockMessages.every((m) => !m.content.trim())
                    ? styles.dialogTestButtonDisabled
                    : {}),
                }}
              >
                {summaryTestDialog.isTesting ? 'Testing...' : 'Test Summary'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Prompt Test Dialog */}
      {analyticsTestDialog.isOpen && (
        <div style={styles.dialogOverlay} onClick={() => setAnalyticsTestDialog((prev) => ({ ...prev, isOpen: false }))}>
          <div style={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogHeader}>
              <h3 style={styles.dialogTitle}>Test Analytics Prompt</h3>
              <button
                onClick={() => setAnalyticsTestDialog((prev) => ({ ...prev, isOpen: false }))}
                style={styles.dialogCloseButton}
              >
                ×
              </button>
            </div>
            <div style={styles.dialogBody}>
              <p style={styles.dialogDescription}>
                Enter a sample user message to test how the analytics prompt extracts data.
              </p>

              {/* User Message Input */}
              <div style={styles.dialogField}>
                <label style={styles.dialogLabel}>Sample User Message</label>
                <textarea
                  value={analyticsTestDialog.userMessage}
                  onChange={(e) => setAnalyticsTestDialog((prev) => ({ ...prev, userMessage: e.target.value }))}
                  placeholder="Enter a sample user message to extract analytics from..."
                  style={styles.dialogTextarea}
                  rows={4}
                  disabled={analyticsTestDialog.isTesting}
                />
              </div>

              {/* Track Goal Completion Toggle */}
              <div style={styles.dialogCheckboxField}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={analyticsTestDialog.trackGoalCompletion}
                    onChange={(e) => setAnalyticsTestDialog((prev) => ({ ...prev, trackGoalCompletion: e.target.checked }))}
                    style={styles.checkbox}
                    disabled={analyticsTestDialog.isTesting}
                  />
                  <span style={styles.checkboxText}>Track Goal Completion</span>
                </label>
              </div>

              {/* Result Display */}
              {analyticsTestDialog.result && (
                <div style={styles.testResultSuccess}>
                  <div style={styles.testResultHeader}>
                    <span style={styles.testResultIcon}>✓</span>
                    <span>Analytics Extracted</span>
                  </div>
                  <pre style={styles.testResultJson}>
                    {JSON.stringify(analyticsTestDialog.result.analytics, null, 2)}
                  </pre>
                  <div style={styles.testResultMetrics}>
                    <div style={styles.testResultMetric}>
                      <span style={styles.testResultMetricLabel}>Model</span>
                      <span style={styles.testResultMetricValue}>{analyticsTestDialog.result.model}</span>
                    </div>
                    <div style={styles.testResultMetric}>
                      <span style={styles.testResultMetricLabel}>Latency</span>
                      <span style={styles.testResultMetricValue}>{analyticsTestDialog.result.latencyMs}ms</span>
                    </div>
                    <div style={styles.testResultMetric}>
                      <span style={styles.testResultMetricLabel}>Tokens</span>
                      <span style={styles.testResultMetricValue}>{analyticsTestDialog.result.usage.totalTokens}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {analyticsTestDialog.error && (
                <div style={styles.testResultError}>
                  <span style={styles.testResultIcon}>✕</span>
                  <span>{analyticsTestDialog.error}</span>
                </div>
              )}
            </div>
            <div style={styles.dialogFooter}>
              <button
                onClick={() => setAnalyticsTestDialog((prev) => ({ ...prev, isOpen: false }))}
                style={styles.dialogCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!analyticsTestDialog.userMessage.trim()) {
                    setAnalyticsTestDialog((prev) => ({
                      ...prev,
                      error: 'Please enter a sample user message.',
                    }));
                    return;
                  }

                  setAnalyticsTestDialog((prev) => ({ ...prev, isTesting: true, result: null, error: null }));
                  testAnalyticsPromptMutation.mutate({
                    analyticsPrompt: analyticsTestDialog.prompt,
                    userMessage: analyticsTestDialog.userMessage,
                    trackGoalCompletion: analyticsTestDialog.trackGoalCompletion,
                  });
                }}
                disabled={analyticsTestDialog.isTesting || !analyticsTestDialog.userMessage.trim()}
                style={{
                  ...styles.dialogTestButton,
                  ...(analyticsTestDialog.isTesting || !analyticsTestDialog.userMessage.trim()
                    ? styles.dialogTestButtonDisabled
                    : {}),
                }}
              >
                {analyticsTestDialog.isTesting ? 'Testing...' : 'Test Analytics'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '800px',
  },
  header: {
    marginBottom: '24px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  tabBar: {
    display: 'flex',
    gap: '0px',
    borderBottom: '2px solid #e2e8f0',
    marginBottom: '24px',
  },
  tabButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  tabButtonActive: {
    color: '#3b82f6',
    borderBottom: '2px solid #3b82f6',
  },
  tabActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '20px',
  },
  successMessage: {
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderRadius: '8px',
    marginBottom: '12px',
    fontSize: '14px',
    border: '1px solid #bbf7d0',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  cardDescription: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  section: {
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f1f5f9',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  subsectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#334155',
    marginBottom: '8px',
    marginTop: '24px',
  },
  sectionDescription: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  warningBox: {
    padding: '16px',
    borderRadius: '10px',
    backgroundColor: '#fff7ed',
    border: '1px solid #fdba74',
  },
  warningText: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    color: '#9a3412',
    lineHeight: '1.5',
  },
  jsonPreview: {
    margin: '0 0 16px 0',
    padding: '12px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fed7aa',
    borderRadius: '8px',
    fontSize: '12px',
    lineHeight: '1.5',
    overflow: 'auto',
    maxHeight: '240px',
    fontFamily: 'monospace',
    color: '#7c2d12',
  },
  warningActionButton: {
    padding: '10px 14px',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #fdba74',
    fontSize: '14px',
    fontWeight: '500',
    color: '#9a3412',
    cursor: 'pointer',
  },
  // Upload styles
  uploadContainer: {
    marginBottom: '8px',
  },
  uploadPreview: {
    position: 'relative' as const,
    display: 'inline-block',
    marginBottom: '12px',
  },
  uploadPreviewImage: {
    maxWidth: '200px',
    maxHeight: '80px',
    objectFit: 'contain' as const,
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    padding: '8px',
  },
  uploadPreviewFavicon: {
    width: '48px',
    height: '48px',
    objectFit: 'contain' as const,
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    padding: '8px',
  },
  uploadRemoveButton: {
    position: 'absolute' as const,
    top: '-8px',
    right: '-8px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#dc2626',
    color: '#ffffff',
    border: 'none',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  uploadInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  uploadLabel: {
    cursor: 'pointer',
    flexShrink: 0,
  },
  uploadInput: {
    display: 'none',
  },
  uploadButton: {
    display: 'inline-block',
    padding: '10px 16px',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  uploadOr: {
    fontSize: '14px',
    color: '#94a3b8',
  },
  uploadError: {
    display: 'block',
    fontSize: '12px',
    color: '#dc2626',
    marginTop: '4px',
  },
  // Color palette styles
  colorPaletteGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  colorField: {
    marginBottom: '8px',
  },
  colorInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  colorPicker: {
    width: '40px',
    height: '40px',
    padding: '0',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
  },
  colorTextInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'monospace',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '16px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    lineHeight: '1.5',
  },
  hint: {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  hintLink: {
    color: '#60a5fa',
    textDecoration: 'underline',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    marginBottom: '6px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#3b82f6',
  },
  checkboxText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  buttonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  toolsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  toolsLoading: {
    padding: '20px',
    textAlign: 'center',
    color: '#64748b',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  toolsError: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  toolsEmpty: {
    padding: '20px',
    textAlign: 'center',
    color: '#64748b',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  toolCard: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  toolHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  toolCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  toolCheckbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  toolName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
  },
  toolBadge: {
    fontSize: '11px',
    color: '#64748b',
    backgroundColor: '#e2e8f0',
    padding: '2px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  toolDescription: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '12px',
    lineHeight: '1.4',
  },
  toolConfigFields: {
    borderTop: '1px solid #e2e8f0',
    paddingTop: '12px',
    marginTop: '4px',
  },
  toolConfigField: {
    marginBottom: '10px',
  },
  toolConfigCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#475569',
    cursor: 'pointer',
  },
  toolConfigCheckbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  toolConfigLabel: {
    display: 'block',
    fontSize: '13px',
    color: '#475569',
    marginBottom: '4px',
  },
  toolConfigInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  toolConfigSelect: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    textTransform: 'capitalize' as const,
  },
  // UI Blocks styles
  blocksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px',
  },
  blockCard: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    borderLeft: '4px solid #0EA5E9',
    transition: 'all 0.2s ease',
    cursor: 'grab',
  },
  blockCardHidden: {
    opacity: 0.5,
    borderLeftColor: '#94a3b8',
  },
  blockCardDragging: {
    opacity: 0.5,
    cursor: 'grabbing',
  },
  blockCardDragOver: {
    borderTop: '3px solid #3b82f6',
    marginTop: '-3px',
  },
  blockDragHandle: {
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'grab',
    userSelect: 'none',
    letterSpacing: '-2px',
    fontWeight: '600',
  },
  blockCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  blockNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#0EA5E9',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  blockCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  blockCheckbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  blockName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
  },
  blockTypeBadge: {
    fontSize: '11px',
    color: '#64748b',
    backgroundColor: '#e2e8f0',
    padding: '2px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  blockActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: 'auto',
  },
  blockActionButton: {
    padding: '4px 8px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#475569',
  },
  blockActionButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  blockDescription: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '8px',
    marginBottom: 0,
    lineHeight: '1.4',
  },
  blocksEmpty: {
    padding: '20px',
    textAlign: 'center',
    color: '#64748b',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px dashed #e2e8f0',
  },
  // Scheduling Mode styles
  schedulingModeSection: {
    borderTop: '1px solid #e2e8f0',
    paddingTop: '12px',
    marginTop: '12px',
  },
  schedulingModeHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  schedulingModeTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
  },
  schedulingModeContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  schedulingModeField: {
    marginBottom: '4px',
  },
  cronHint: {
    display: 'block',
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  // Voice Configuration styles
  voiceConfigSection: {
    marginTop: '8px',
  },
  voiceConfigFields: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  speedInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  speedSlider: {
    flex: 1,
    height: '6px',
    cursor: 'pointer',
    accentColor: '#3b82f6',
  },
  speedInput: {
    width: '80px',
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  voicePreviewInfo: {
    marginTop: '16px',
    padding: '12px 16px',
    backgroundColor: '#eff6ff',
    borderRadius: '8px',
    border: '1px solid #bfdbfe',
  },
  voicePreviewLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: '4px',
  },
  voicePreviewText: {
    fontSize: '14px',
    color: '#1e3a8a',
  },
  // Quick Response styles
  quickResponseSection: {
    marginTop: '8px',
  },
  quickResponseFields: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  quickResponseTestSection: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  quickResponseTestInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  quickResponseTestResults: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #bbf7d0',
  },
  quickResponseTestResultHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#166534',
    marginBottom: '12px',
  },
  quickResponseTestResponse: {
    fontSize: '16px',
    fontStyle: 'italic',
    color: '#1e293b',
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    marginBottom: '16px',
  },
  quickResponseTestMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  quickResponseTestMetricItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  quickResponseTestMetricLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  quickResponseTestMetricValue: {
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: '500',
  },
  rawToggleButton: {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  rawDataSection: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  rawDataBlock: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  rawDataHeader: {
    padding: '8px 12px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  rawDataContent: {
    padding: '12px',
    margin: 0,
    backgroundColor: '#ffffff',
    fontSize: '12px',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    color: '#334155',
    overflow: 'auto',
    maxHeight: '300px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
  // Telegram Integration styles
  telegramSection: {
    marginTop: '8px',
  },
  telegramInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  telegramInput: {
    flex: 1,
  },
  verifyButton: {
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  clearButton: {
    padding: '8px 12px',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    lineHeight: 1,
  },
  inputSuccess: {
    borderColor: '#22c55e',
    backgroundColor: '#f0fdf4',
  },
  inputError: {
    borderColor: '#ef4444',
    backgroundColor: '#fef2f2',
  },
  telegramSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  successIcon: {
    color: '#22c55e',
    fontSize: '16px',
    fontWeight: '600',
  },
  telegramError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  errorIcon: {
    color: '#ef4444',
    fontSize: '16px',
    fontWeight: '600',
  },
  telegramHint: {
    padding: '12px 16px',
    backgroundColor: '#fffbeb',
    color: '#92400e',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  // Webhook Status Panel styles
  webhookStatusPanel: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  webhookStatusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  webhookStatusTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#334155',
    margin: 0,
  },
  checkWebhookButton: {
    padding: '8px 14px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  webhookError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '6px',
    fontSize: '13px',
  },
  webhookInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  webhookInfoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  webhookInfoLabel: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '500',
    minWidth: '120px',
    flexShrink: 0,
  },
  webhookInfoValue: {
    fontSize: '13px',
    color: '#1e293b',
    wordBreak: 'break-all',
  },
  webhookNoValue: {
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  webhookWarningValue: {
    color: '#d97706',
    fontWeight: '500',
  },
  webhookErrorInfo: {
    marginTop: '8px',
    padding: '10px 12px',
    backgroundColor: '#fef2f2',
    borderRadius: '6px',
    border: '1px solid #fecaca',
  },
  webhookErrorValue: {
    fontSize: '13px',
    color: '#dc2626',
    wordBreak: 'break-all',
  },
  webhookHint: {
    padding: '10px 14px',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    borderRadius: '6px',
    fontSize: '13px',
    textAlign: 'center',
  },
  // WhatsApp Integration styles
  whatsappSection: {
    marginTop: '8px',
  },
  whatsappInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  whatsappInput: {
    flex: 1,
  },
  whatsappActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginTop: '16px',
  },
  whatsappSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  whatsappError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  whatsappHint: {
    padding: '12px 16px',
    backgroundColor: '#fffbeb',
    color: '#92400e',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  // Webhook Configuration styles
  webhookConfigSection: {
    marginTop: '20px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  webhookConfigTitle: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  webhookConfigDescription: {
    margin: '0 0 16px 0',
    fontSize: '13px',
    color: '#64748b',
  },
  webhookUrlContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  readOnlyInput: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    cursor: 'default',
    flex: 1,
  },
  copyButton: {
    padding: '8px 12px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  generateButton: {
    padding: '8px 12px',
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  // Payment integration styles
  paymentSection: {
    marginTop: '8px',
  },
  paymentInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  paymentInput: {
    flex: 1,
  },
  paymentIcon: {
    marginRight: '8px',
  },
  helpLink: {
    color: '#3b82f6',
    textDecoration: 'none',
  },
  testModeToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  testModeBadge: {
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  testModeBadgeTest: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: '1px solid #f59e0b',
  },
  testModeBadgeLive: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '1px solid #22c55e',
  },
  paymentSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  paymentError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  paymentHint: {
    padding: '12px 16px',
    backgroundColor: '#fffbeb',
    color: '#92400e',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  accountId: {
    fontSize: '12px',
    color: '#64748b',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  // Analytics & Summaries styles
  analyticsConfigSection: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  // Label with button row
  labelWithButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  // Test Prompt Button
  testPromptButton: {
    padding: '6px 12px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  // Hint with button row
  hintWithButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginTop: '4px',
  },
  // Use Default Button
  useDefaultButton: {
    padding: '4px 10px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'background-color 0.15s ease',
    flexShrink: 0,
  },
  // Dialog Styles
  dialogOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  dialogContent: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
  },
  dialogTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  dialogCloseButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '20px',
    color: '#64748b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogBody: {
    padding: '24px',
  },
  dialogDescription: {
    fontSize: '14px',
    color: '#64748b',
    lineHeight: '1.5',
    marginTop: 0,
    marginBottom: '20px',
  },
  dialogField: {
    marginBottom: '16px',
  },
  dialogLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '8px',
  },
  dialogTextarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#1e293b',
    resize: 'vertical' as const,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  dialogCheckboxField: {
    marginBottom: '16px',
  },
  dialogFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  dialogCancelButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
    cursor: 'pointer',
  },
  dialogTestButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    backgroundColor: '#6366f1',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  dialogTestButtonDisabled: {
    backgroundColor: '#c7d2fe',
    cursor: 'not-allowed',
  },
  // Mock Messages Section
  mockMessagesSection: {
    marginBottom: '16px',
  },
  mockMessageRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    alignItems: 'center',
  },
  mockMessageRoleSelect: {
    width: '100px',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#1e293b',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
  },
  mockMessageInput: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#1e293b',
    outline: 'none',
  },
  removeMessageButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    fontSize: '16px',
    color: '#64748b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addMessageButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    cursor: 'pointer',
  },
  // User Search Section Styles
  userSearchSection: {
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #e2e8f0',
  },
  userSearchInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  userSearchInput: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#1e293b',
    outline: 'none',
  },
  clearUserButton: {
    padding: '10px 16px',
    borderRadius: '8px',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
    cursor: 'pointer',
  },
  selectedUserBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
    padding: '8px 12px',
    backgroundColor: '#eff6ff',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#1e40af',
  },
  selectedUserIcon: {
    fontSize: '14px',
  },
  userSearchResults: {
    marginTop: '8px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    maxHeight: '200px',
    overflowY: 'auto' as const,
  },
  userSearchResultItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  userSearchResultName: {
    color: '#1e293b',
    fontWeight: '500',
  },
  userSearchResultPhone: {
    color: '#64748b',
    fontSize: '13px',
  },
  userSearchLoading: {
    padding: '12px',
    textAlign: 'center' as const,
    color: '#64748b',
    fontSize: '14px',
  },
  userSearchEmpty: {
    padding: '12px',
    textAlign: 'center' as const,
    color: '#64748b',
    fontSize: '14px',
  },
  loadingUserMessages: {
    marginTop: '8px',
    padding: '8px 12px',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#64748b',
    textAlign: 'center' as const,
  },
  // Test Result Styles
  testResultSuccess: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #bbf7d0',
  },
  testResultError: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#991b1b',
    fontSize: '14px',
  },
  testResultHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#166534',
    marginBottom: '12px',
  },
  testResultIcon: {
    fontSize: '16px',
  },
  testResultContent: {
    fontSize: '14px',
    color: '#1e293b',
    lineHeight: '1.6',
    padding: '12px',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    marginBottom: '12px',
    whiteSpace: 'pre-wrap' as const,
  },
  testResultJson: {
    fontSize: '12px',
    color: '#1e293b',
    lineHeight: '1.5',
    padding: '12px',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    marginBottom: '12px',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    overflow: 'auto',
    maxHeight: '200px',
    margin: 0,
  },
  testResultMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  },
  testResultMetric: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  testResultMetricLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  testResultMetricValue: {
    fontSize: '14px',
    color: '#166534',
    fontWeight: '500',
  },
  // Plans Tab styles
  emptyState: {
    padding: '32px',
    textAlign: 'center' as const,
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px dashed #e2e8f0',
  },
  emptyStateText: {
    fontSize: '15px',
    color: '#475569',
    marginBottom: '8px',
    fontWeight: '500',
  },
  emptyStateHint: {
    fontSize: '13px',
    color: '#94a3b8',
    margin: 0,
  },
  plansList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  planCard: {
    padding: '20px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  planHeader: {
    marginBottom: '8px',
  },
  planNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
    flexWrap: 'wrap' as const,
  },
  planName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
  },
  planKey: {
    fontSize: '12px',
    color: '#64748b',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  planBadge: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#ffffff',
    backgroundColor: '#3b82f6',
    padding: '2px 8px',
    borderRadius: '4px',
    textTransform: 'capitalize' as const,
  },
  planDescription: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  planDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
  },
  planPrice: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
  },
  planInterval: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#64748b',
  },
  planTrial: {
    fontSize: '13px',
    color: '#059669',
    backgroundColor: '#d1fae5',
    padding: '4px 10px',
    borderRadius: '4px',
    fontWeight: '500',
  },
  planFeatures: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  planFeatureItem: {
    fontSize: '13px',
    color: '#475569',
    padding: '4px 0',
    paddingLeft: '20px',
    position: 'relative' as const,
  },
  planFeatureMore: {
    fontSize: '12px',
    color: '#94a3b8',
    fontStyle: 'italic',
    padding: '4px 0',
    paddingLeft: '20px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '16px',
  },
  createPlanButton: {
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  planActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  planEditButton: {
    padding: '8px 16px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  planDeleteButton: {
    padding: '8px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  planMoveButton: {
    padding: '6px 10px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '32px',
  },
  planMoveButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  planReorderButtons: {
    display: 'flex',
    gap: '4px',
    marginLeft: 'auto',
  },
};

export default BotEditPage;
